import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
  Img,
} from 'remotion';

// 设计基准尺寸：所有模板内部坐标都基于 1280×720 布局，
// 外层用 useVideoConfig 实际尺寸等比缩放以适配任意合成宽高。
const BASE_W = 1280;
const BASE_H = 720;

// 统一的内容缩放舞台：保证所有模板在不同合成尺寸下等比居中显示、
// 不裁切、不拉伸（取最小缩放比）。
const ScaledStage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { width, height } = useVideoConfig();
  const scale = Math.min(width / BASE_W, height / BASE_H);
  const contentW = BASE_W * scale;
  const contentH = BASE_H * scale;
  const left = (width - contentW) / 2;
  const top = (height - contentH) / 2;
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width: BASE_W,
        height: BASE_H,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
    >
      {children}
    </div>
  );
};

// 精选示例图片（按"由大到小"排布以获得更平衡的瀑布流）
// URL 末尾的 w/h 决定宽高比；这里使用 800x### 的控制方式
const SAMPLE_IMAGES = [
  'https://picsum.photos/seed/luminance/800/1020', // 纵幅 · 1.28
  'https://picsum.photos/seed/bloom/800/990',      // 纵幅 · 1.24
  'https://picsum.photos/seed/reverie/800/940',    // 纵幅 · 1.17
  'https://picsum.photos/seed/stillness/800/800',  // 方形 · 1.0
  'https://picsum.photos/seed/horizon/800/540',    // 横幅 · 0.67
  'https://picsum.photos/seed/echo/800/540',       // 横幅 · 0.67
  'https://picsum.photos/seed/drift/800/540',      // 横幅 · 0.67
  'https://picsum.photos/seed/afterlight/800/540', // 横幅 · 0.67
];

const SAMPLE_CAPTIONS = [
  'Luminance',
  'Bloom',
  'Reverie',
  'Stillness',
  'Horizon',
  'Echo',
  'Drift',
  'Afterlight',
];

// #118 瀑布流图片墙（ImageMasonryGrid）
// 优化点：
// 1. 增加标题 / 副标题区域，形成画廊式视觉层级
// 2. 4 列 + 精心控制的宽高比，避免底部截断
// 3. 自动缩放 (scaleToFit) 机制——即便传入任意图片也能优雅适配
// 4. 每张图附带编号 + caption + 渐变蒙版
// 5. 深色渐变背景 + 细纹理，更有展厅氛围
// 6. 错位 spring + 位移 + 淡入，入场更有层次感
export const ImageMasonryGrid: React.FC<{
  images?: string[];
  captions?: string[];
  columns?: number;
  gap?: number;
  title?: string;
  subtitle?: string;
}> = ({
  images = SAMPLE_IMAGES,
  captions = SAMPLE_CAPTIONS,
  columns = 4,
  gap = 14,
  title = 'Curated Gallery',
  subtitle = 'A Masonry Exhibition · Moments in Frame',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 头部区域与画面内边距
  const headerHeight = 110;
  const paddingX = 28;
  const paddingBottom = 24;

  const columnWidth =
    (width - paddingX * 2 - gap * (columns - 1)) / columns;
  const gridAvailableHeight = height - headerHeight - paddingBottom;

  // 瀑布流分配（最短列优先）
  const columnHeights = Array(columns).fill(0);
  const imagePositions: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    image: string;
    caption: string;
    index: number;
    delay: number;
  }> = [];

  images.forEach((image, i) => {
    const shortestColumn = columnHeights.indexOf(Math.min(...columnHeights));

    const urlParts = image.split('/');
    const widthInUrl = parseInt(urlParts[urlParts.length - 2]) || 800;
    const heightInUrl = parseInt(urlParts[urlParts.length - 1]) || 600;
    const aspectRatio = heightInUrl / widthInUrl;
    const imageHeight = columnWidth * aspectRatio;

    imagePositions.push({
      x: paddingX + shortestColumn * (columnWidth + gap),
      y: columnHeights[shortestColumn],
      width: columnWidth,
      height: imageHeight,
      image,
      caption: captions[i] ?? '',
      index: i + 1,
      delay: 8 + i * 4,
    });

    columnHeights[shortestColumn] += imageHeight + gap;
  });

  // 自动缩放：若总高超出可视区，则整体缩放以适配
  const maxColumnHeight = Math.max(...columnHeights) - gap;
  const scaleToFit =
    maxColumnHeight > gridAvailableHeight
      ? gridAvailableHeight / maxColumnHeight
      : 1;
  const scaledHeight = maxColumnHeight * scaleToFit;
  const gridTopOffset =
    headerHeight + (gridAvailableHeight - scaledHeight) / 2;

  // 标题入场
  const titleEnter = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140, mass: 0.8 },
  });
  const titleOpacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(titleEnter, [0, 1], [-20, 0]);
  const dividerWidth = interpolate(titleEnter, [0, 1], [0, 200]);

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at top, #1b2040 0%, #0f1427 55%, #080b1a 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景细纹纹理 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(45deg, rgba(255,255,255,0.015) 0px, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 8px)',
          pointerEvents: 'none',
        }}
      />

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 26,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 30,
            fontWeight: 400,
            color: '#f4e6c3',
            letterSpacing: 10,
            textTransform: 'uppercase',
            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginTop: 10,
          }}
        >
          <div
            style={{
              width: dividerWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
          <div
            style={{
              fontFamily: '"Georgia", serif',
              fontSize: 12,
              color: '#c9b489',
              letterSpacing: 5,
              textTransform: 'uppercase',
              fontStyle: 'italic',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
          <div
            style={{
              width: dividerWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
        </div>
      </div>

      {/* 瀑布流网格容器：自动缩放以适配可视区 */}
      <div
        style={{
          position: 'absolute',
          top: gridTopOffset,
          left: 0,
          width,
          height: maxColumnHeight,
          transform: `scale(${scaleToFit})`,
          transformOrigin: '50% 0%',
        }}
      >
        {imagePositions.map((pos, i) => {
          const scale = spring({
            frame: frame - pos.delay,
            fps,
            config: { damping: 14, stiffness: 110, mass: 0.6 },
          });

          const opacity = interpolate(frame - pos.delay, [0, 14], [0, 1], {
            extrapolateRight: 'clamp',
          });

          const translateY = interpolate(scale, [0, 1], [36, 0]);

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                width: pos.width,
                height: pos.height,
                opacity,
                transform: `translateY(${translateY}px) scale(${
                  0.92 + scale * 0.08
                })`,
                transformOrigin: '50% 60%',
                overflow: 'hidden',
                borderRadius: 10,
                boxShadow:
                  '0 16px 36px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35)',
              }}
            >
              <img
                src={pos.image}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                }}
              />

              {/* 底部渐变蒙版 + 文案 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: '28px 14px 12px',
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 100%)',
                  color: '#f4e6c3',
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontFamily:
                      '"SF Mono", "Menlo", "Courier New", monospace',
                    fontSize: 10,
                    letterSpacing: 2,
                    opacity: 0.75,
                  }}
                >
                  №{String(pos.index).padStart(2, '0')}
                </span>
                <span
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 15,
                    letterSpacing: 1.2,
                    fontStyle: 'italic',
                    textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                  }}
                >
                  {pos.caption}
                </span>
              </div>

              {/* 顶部细金边 */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 1,
                  background:
                    'linear-gradient(90deg, transparent, rgba(244,230,195,0.6), transparent)',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* 底部页脚标识 */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: '"Georgia", serif',
          fontSize: 10,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: 'rgba(244,230,195,0.35)',
          opacity: titleOpacity,
        }}
      >
        MMXXVI · Volume I
      </div>
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #119 网格拼贴画
const GRID_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/grid-1/800/800',
  'https://picsum.photos/seed/grid-2/800/800',
  'https://picsum.photos/seed/grid-3/800/800',
  'https://picsum.photos/seed/grid-4/800/800',
  'https://picsum.photos/seed/grid-5/800/800',
  'https://picsum.photos/seed/grid-6/800/800',
  'https://picsum.photos/seed/grid-7/800/800',
  'https://picsum.photos/seed/grid-8/800/800',
  'https://picsum.photos/seed/grid-9/800/800',
];

const GRID_DEFAULT_CAPTIONS = [
  'Dawn',
  'Bloom',
  'Drift',
  'Echo',
  'Focus',
  'Glow',
  'Horizon',
  'Muse',
  'Nova',
];

export const ImageGridCollage: React.FC<{
  images?: string[];
  captions?: string[];
  rows?: number;
  cols?: number;
  title?: string;
  subtitle?: string;
}> = ({
  images = GRID_DEFAULT_IMAGES,
  captions = GRID_DEFAULT_CAPTIONS,
  rows = 3,
  cols = 3,
  title = 'Grid Collage',
  subtitle = 'Nine Frames · One Story',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 顶部标题区高度
  const headerHeight = 92;
  const padding = 20;
  const gap = 10;

  // 标题入场
  const titleEnter = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140, mass: 0.8 },
  });
  const titleOpacity = interpolate(frame, [0, 18], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(titleEnter, [0, 1], [-20, 0]);
  const lineWidth = interpolate(titleEnter, [0, 1], [0, 160]);

  const total = rows * cols;
  const centerRow = Math.floor(rows / 2);
  const centerCol = Math.floor(cols / 2);

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(135deg, #151a2e 0%, #0f1628 50%, #0a1020 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景细纹纹理 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(45deg, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 7px)',
          pointerEvents: 'none',
        }}
      />

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 0,
          right: 0,
          height: headerHeight - 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 32,
            fontWeight: 700,
            color: '#f4e6c3',
            letterSpacing: 6,
            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginTop: 6,
          }}
        >
          <div
            style={{
              width: lineWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
          <div
            style={{
              fontFamily: '"Georgia", serif',
              fontSize: 12,
              color: '#c9b489',
              letterSpacing: 5,
              textTransform: 'uppercase',
              fontStyle: 'italic',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
          <div
            style={{
              width: lineWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
        </div>
      </div>

      {/* 网格 */}
      <div
        style={{
          position: 'absolute',
          top: headerHeight,
          left: padding,
          right: padding,
          bottom: padding,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap,
        }}
      >
        {Array.from({ length: total }).map((_, i) => {
          const row = Math.floor(i / cols);
          const col = i % cols;
          const isCenter = row === centerRow && col === centerCol;

          // 对角线波浪入场：按 row + col 排序
          const diag = row + col;
          const delay = 8 + diag * 5 + (col % 2) * 2;

          const enter = spring({
            frame: frame - delay,
            fps,
            config: { damping: 15, stiffness: 120, mass: 0.7 },
          });
          const opacity = interpolate(frame - delay, [0, 12], [0, 1], {
            extrapolateRight: 'clamp',
          });

          // 奇偶行方向不同：偶数行从左翻入，奇数行从右
          const flipSign = row % 2 === 0 ? 1 : -1;
          const rotY = interpolate(enter, [0, 1], [90 * flipSign, 0]);
          const scale = interpolate(enter, [0, 1], [0.7, 1]);

          // 入场后轻微浮动
          const idle = Math.max(0, frame - delay - 24);
          const floatY = Math.sin((idle / fps) * 1.1 + i * 0.6) * 1.8;

          // 注解延迟入场
          const captionOpacity = interpolate(
            frame - delay - 10,
            [0, 12],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );
          const captionY = interpolate(
            frame - delay - 10,
            [0, 18],
            [8, 0],
            { extrapolateRight: 'clamp' }
          );

          const image = images[i % images.length];
          const caption = captions[i % captions.length];

          return (
            <div
              key={i}
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 12,
                opacity,
                transform: `translateY(${floatY}px) scale(${scale}) rotateY(${rotY}deg)`,
                transformOrigin: 'center',
                background: '#000',
                boxShadow: isCenter
                  ? '0 14px 36px rgba(0,0,0,0.55), 0 0 0 2px rgba(244,230,195,0.35) inset'
                  : '0 8px 22px rgba(0,0,0,0.4), 0 0 0 1px rgba(244,230,195,0.1) inset',
              }}
            >
              <img
                src={image}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: isCenter
                    ? 'saturate(1.12) contrast(1.05)'
                    : 'saturate(1.02) contrast(1.02)',
                }}
              />

              {/* 顶部微光 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  height: '30%',
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 100%)',
                  pointerEvents: 'none',
                }}
              />

              {/* 底部渐变 + 注解 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: '12px 12px 10px',
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.78) 100%)',
                  opacity: captionOpacity,
                  transform: `translateY(${captionY}px)`,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 14,
                    color: '#f4e6c3',
                    letterSpacing: 1.5,
                    fontStyle: 'italic',
                    textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                  }}
                >
                  {caption}
                </div>
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 9,
                    color: '#c9b489',
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}/{String(total).padStart(2, '0')}
                </div>
              </div>

              {/* 中心格：金色内边框强调 */}
              {isCenter && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 5,
                    border: '1px solid rgba(244,230,195,0.45)',
                    borderRadius: 9,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* 边缘暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 58%, rgba(0,0,0,0.42) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #120 砖石拼图效果
const BRICK_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/brick-a/900/900',
  'https://picsum.photos/seed/brick-b/600/600',
  'https://picsum.photos/seed/brick-c/600/600',
  'https://picsum.photos/seed/brick-d/900/450',
  'https://picsum.photos/seed/brick-e/450/900',
  'https://picsum.photos/seed/brick-f/600/600',
  'https://picsum.photos/seed/brick-g/600/600',
  'https://picsum.photos/seed/brick-h/900/900',
];

const BRICK_DEFAULT_CAPTIONS = [
  'Sunrise',
  'Forest',
  'Ocean',
  'Skyline',
  'Mountain',
  'Desert',
  'Harbor',
  'Aurora',
];

export const ImageBrickMosaic: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = BRICK_DEFAULT_IMAGES,
  captions = BRICK_DEFAULT_CAPTIONS,
  title = 'Mosaic Stories',
  subtitle = 'A curated collection · 2026',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 4×4 砖块布局（共填满 16 格），方向用于入场时的切入方向
  const bricks: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
    from: 'top' | 'bottom' | 'left' | 'right';
  }> = [
    { x: 0, y: 0, w: 2, h: 2, from: 'left' },
    { x: 2, y: 0, w: 1, h: 1, from: 'top' },
    { x: 3, y: 0, w: 1, h: 1, from: 'right' },
    { x: 2, y: 1, w: 2, h: 1, from: 'right' },
    { x: 0, y: 2, w: 1, h: 2, from: 'left' },
    { x: 1, y: 2, w: 1, h: 1, from: 'bottom' },
    { x: 1, y: 3, w: 1, h: 1, from: 'bottom' },
    { x: 2, y: 2, w: 2, h: 2, from: 'right' },
  ];

  // 预留顶部标题区域
  const headerHeight = 96;
  const padding = 16;
  const gap = 10;

  // 标题入场
  const titleEnter = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140, mass: 0.8 },
  });
  const titleOpacity = interpolate(frame, [0, 18], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(titleEnter, [0, 1], [-20, 0]);

  const lineWidth = interpolate(titleEnter, [0, 1], [0, 180]);

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景纹理 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(45deg, rgba(255,255,255,0.015) 0px, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 6px)',
          pointerEvents: 'none',
        }}
      />

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 0,
          right: 0,
          height: headerHeight - 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 34,
            fontWeight: 700,
            color: '#f4e6c3',
            letterSpacing: 6,
            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginTop: 6,
          }}
        >
          <div
            style={{
              width: lineWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
          <div
            style={{
              fontFamily: '"Georgia", serif',
              fontSize: 13,
              color: '#c9b489',
              letterSpacing: 6,
              textTransform: 'uppercase',
              fontStyle: 'italic',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
          <div
            style={{
              width: lineWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
        </div>
      </div>

      {/* 砖块网格 */}
      <div
        style={{
          position: 'absolute',
          top: headerHeight,
          left: padding,
          right: padding,
          bottom: padding,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridTemplateRows: 'repeat(4, 1fr)',
          gap,
        }}
      >
        {bricks.map((brick, i) => {
          const delay = 8 + i * 5;
          const enter = spring({
            frame: frame - delay,
            fps,
            config: { damping: 14, stiffness: 110, mass: 0.8 },
          });

          const opacity = interpolate(frame - delay, [0, 14], [0, 1], {
            extrapolateRight: 'clamp',
          });

          // 根据方向决定入场偏移
          let tx = 0;
          let ty = 0;
          const offset = 140;
          if (brick.from === 'left') tx = -offset;
          if (brick.from === 'right') tx = offset;
          if (brick.from === 'top') ty = -offset;
          if (brick.from === 'bottom') ty = offset;
          const translateX = interpolate(enter, [0, 1], [tx, 0]);
          const translateY = interpolate(enter, [0, 1], [ty, 0]);
          const scale = interpolate(enter, [0, 1], [0.85, 1]);

          // 入场后轻微呼吸浮动
          const idle = Math.max(0, frame - delay - 20);
          const floatY = Math.sin((idle / fps) * 1.1 + i) * 2;

          // 注解条入场延迟再多一点
          const captionOpacity = interpolate(
            frame - delay - 12,
            [0, 12],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );
          const captionY = interpolate(
            frame - delay - 12,
            [0, 18],
            [10, 0],
            { extrapolateRight: 'clamp' }
          );

          const img = images[i % images.length];
          const caption = captions[i % captions.length];
          const isLarge = brick.w >= 2 && brick.h >= 2;

          return (
            <div
              key={i}
              style={{
                gridColumn: `${brick.x + 1} / span ${brick.w}`,
                gridRow: `${brick.y + 1} / span ${brick.h}`,
                opacity,
                transform: `translate(${translateX}px, ${
                  translateY + floatY
                }px) scale(${scale})`,
                overflow: 'hidden',
                borderRadius: 14,
                boxShadow:
                  '0 10px 28px rgba(0,0,0,0.45), 0 0 0 1px rgba(244,230,195,0.12) inset',
                position: 'relative',
                background: '#000',
              }}
            >
              <img
                src={img}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'saturate(1.05) contrast(1.02)',
                }}
              />

              {/* 顶部微光 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  height: '28%',
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 100%)',
                  pointerEvents: 'none',
                }}
              />

              {/* 底部渐变 + 注解 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: isLarge ? '18px 16px 14px' : '14px 12px 10px',
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.75) 100%)',
                  opacity: captionOpacity,
                  transform: `translateY(${captionY}px)`,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: isLarge ? 11 : 9,
                    color: '#c9b489',
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}
                >
                  {String(i + 1).padStart(2, '0')} / {bricks.length}
                </div>
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: isLarge ? 22 : 14,
                    color: '#f4e6c3',
                    letterSpacing: 2,
                    fontStyle: 'italic',
                    textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                  }}
                >
                  {caption}
                </div>
              </div>

              {/* 金色边框装饰（仅大块） */}
              {isLarge && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 6,
                    border: '1px solid rgba(244,230,195,0.28)',
                    borderRadius: 10,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* 边缘暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.4) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #121 旋转轮播图片墙
export const ImageRotatingGallery: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = [
    'https://picsum.photos/seed/g1/600/600',
    'https://picsum.photos/seed/g2/600/600',
    'https://picsum.photos/seed/g3/600/600',
    'https://picsum.photos/seed/g4/600/600',
    'https://picsum.photos/seed/g5/600/600',
    'https://picsum.photos/seed/g6/600/600',
    'https://picsum.photos/seed/g7/600/600',
    'https://picsum.photos/seed/g8/600/600',
  ],
  captions = [
    'Horizon',
    'Voyage',
    'Moments',
    'Serenity',
    'Adventure',
    'Reflection',
    'Wonder',
    'Legacy',
  ],
  title = 'Gallery 2026',
  subtitle = 'A Rotating Retrospective',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const count = images.length;
  const angleStep = 360 / count;
  const radius = 360;
  const itemW = 240;
  const itemH = 240;

  // 整体入场：淡入 + 放大
  const enter = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 80, mass: 1 },
  });
  const groupScale = interpolate(enter, [0, 1], [0.55, 1]);

  // 旋转速度缓起：前 30 帧角速度从 0 线性提升到 1.4 deg/frame，
  // 之后匀速。积分后：启动段位移 = frame^2/60 * 1.4，30 帧末累积 21 度
  const rotY =
    frame < 30
      ? ((frame * frame) / 60) * 1.4
      : 21 + (frame - 30) * 1.4;

  // 标题入场
  const titleOpacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(enter, [0, 1], [-20, 0]);

  // 当前正前方的图片索引（用于展示标题）
  const normalized = ((rotY % 360) + 360) % 360;
  // 正面图片：angle + rotY ≈ 0 时在前方（我们让 angle=0 图片在 rotY=0 时位于前方）
  // 因此 front index = round((360 - normalized) / angleStep) % count
  const frontIndex = Math.round((360 - normalized) / angleStep) % count;

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 45%, #2a2150 0%, #140a2e 55%, #07031a 100%)',
        perspective: 1400,
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 星点装饰 */}
      {Array.from({ length: 60 }).map((_, i) => {
        const sx = (i * 97) % width;
        const sy = (i * 53) % height;
        const tw = (Math.sin((frame + i * 7) / 15) + 1) / 2;
        const size = (i % 3) + 1;
        return (
          <div
            key={`star-${i}`}
            style={{
              position: 'absolute',
              left: sx,
              top: sy,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#ffffff',
              opacity: 0.15 + tw * 0.5,
              boxShadow: '0 0 4px rgba(255,255,255,0.6)',
            }}
          />
        );
      })}

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 48,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
          zIndex: 20,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 44,
            fontWeight: 700,
            color: '#f4e6c3',
            letterSpacing: 6,
            textShadow: '0 2px 12px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontSize: 16,
            color: '#c9b489',
            letterSpacing: 8,
            marginTop: 6,
            textTransform: 'uppercase',
            fontStyle: 'italic',
            opacity: 0.85,
          }}
        >
          {subtitle}
        </div>
        {/* 分割线 */}
        <div
          style={{
            margin: '14px auto 0',
            width: 120,
            height: 1,
            background:
              'linear-gradient(90deg, transparent, rgba(244,230,195,0.8), transparent)',
          }}
        />
      </div>

      {/* 中心轮播（外层：静态定位 + 整体缩放入场） */}
      <div
        style={{
          position: 'absolute',
          left: width / 2,
          top: height / 2,
          width: 0,
          height: 0,
          transformStyle: 'preserve-3d',
          transform: `scale(${groupScale})`,
        }}
      >
        {/* 旋转容器（仅负责 3D 旋转，不包含地面反射） */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            transformStyle: 'preserve-3d',
            transform: `rotateX(-8deg) rotateY(${rotY}deg)`,
          }}
        >
          {images.map((image, i) => {
            const angle = angleStep * i;
            const radian = (angle * Math.PI) / 180;
            const x = Math.sin(radian) * radius;
            const z = Math.cos(radian) * radius;

            // 当前图片在世界坐标中的实际 z 深度（旋转后）
            const worldAngle = (angle + rotY) * (Math.PI / 180);
            const worldZ = Math.cos(worldAngle); // -1(最远) ~ 1(最近)

            // 根据深度调整亮度与对比度：近亮远暗
            const brightness = interpolate(worldZ, [-1, 1], [0.45, 1]);
            const depthOpacity = interpolate(worldZ, [-1, 1], [0.55, 1]);
            const shadowStrength = interpolate(worldZ, [-1, 1], [0.2, 0.7]);

            // 单张卡入场延迟（环绕依次显现）
            const itemEnter = spring({
              frame: frame - 6 - i * 2,
              fps,
              config: { damping: 16, stiffness: 120, mass: 0.7 },
            });
            const itemOpacity = interpolate(
              frame - 6 - i * 2,
              [0, 12],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );

            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: itemW,
                  height: itemH,
                  left: -itemW / 2,
                  top: -itemH / 2,
                  transform: `translateX(${x}px) translateZ(${z}px) rotateY(${-rotY}deg) scale(${itemEnter})`,
                  opacity: itemOpacity * depthOpacity,
                  borderRadius: 18,
                  overflow: 'hidden',
                  border: '2px solid rgba(244,230,195,0.9)',
                  boxShadow: `0 ${18 * shadowStrength}px ${
                    40 * shadowStrength
                  }px rgba(0,0,0,${shadowStrength}), 0 0 0 1px rgba(244,230,195,0.25) inset`,
                  background: '#000',
                }}
              >
                <img
                  src={image}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: `brightness(${brightness}) saturate(1.05)`,
                  }}
                />
                {/* 顶部光泽 */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    height: '35%',
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* 地面反射圆环（玻璃舞台感） */}
        <div
          style={{
            position: 'absolute',
            left: -radius - 80,
            top: itemH / 2 + 10,
            width: (radius + 80) * 2,
            height: 140,
            borderRadius: '50%',
            background:
              'radial-gradient(ellipse at center, rgba(200,180,130,0.25) 0%, rgba(200,180,130,0.08) 40%, transparent 70%)',
            transform: 'rotateX(80deg)',
            filter: 'blur(6px)',
          }}
        />
      </div>

      {/* 当前正面图片的标注 */}
      <div
        style={{
          position: 'absolute',
          bottom: 55,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleOpacity,
          zIndex: 20,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontSize: 13,
            color: '#a99466',
            letterSpacing: 6,
            textTransform: 'uppercase',
          }}
        >
          Now Showing
        </div>
        <div
          key={frontIndex}
          style={{
            fontFamily: '"Georgia", serif',
            fontSize: 30,
            color: '#f4e6c3',
            letterSpacing: 3,
            marginTop: 4,
            fontStyle: 'italic',
            textShadow: '0 2px 10px rgba(0,0,0,0.6)',
          }}
        >
          {captions[frontIndex % captions.length]}
        </div>
      </div>

      {/* 底部进度指示点 */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
          opacity: titleOpacity,
          zIndex: 20,
        }}
      >
        {images.map((_, i) => {
          const active = i === frontIndex;
          return (
            <div
              key={i}
              style={{
                width: active ? 26 : 8,
                height: 8,
                borderRadius: 4,
                background: active
                  ? 'linear-gradient(90deg, #f4e6c3, #c9a65a)'
                  : 'rgba(244,230,195,0.3)',
                transition: 'all 0.2s',
                boxShadow: active ? '0 0 8px rgba(244,230,195,0.6)' : 'none',
              }}
            />
          );
        })}
      </div>

      {/* 画面边缘暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #122 随机堆叠照片墙（拍立得记忆墙）
export const ImageScatteredPhotos: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = SAMPLE_IMAGES,
  captions = ['Summer', 'Together', 'Sunset', 'Adventure', 'Memories', 'Forever'],
  title = 'Our Memories',
  subtitle = 'A collection of moments ·  2026',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 手工微调的自然散落布局（坐标相对 1280×720 设计区，外层统一缩放）
  // 照片尺寸 240×280（含拍立得白框），设计安全区 [20, 1260] × [120, 700]
  const layout = [
    { cx: 230, cy: 280, rot: -9, z: 1 },
    { cx: 480, cy: 220, rot: 6, z: 3 },
    { cx: 760, cy: 280, rot: -4, z: 2 },
    { cx: 1030, cy: 230, rot: 8, z: 4 },
    { cx: 360, cy: 530, rot: 5, z: 5 },
    { cx: 650, cy: 560, rot: -7, z: 6 },
    { cx: 930, cy: 530, rot: 4, z: 5 },
    { cx: 1150, cy: 570, rot: -10, z: 4 },
  ];

  const photos = images.slice(0, layout.length).map((image, i) => ({
    image,
    caption: captions[i % captions.length],
    ...layout[i],
    delay: 6 + i * 5,
  }));

  // 标题入场进度
  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140, mass: 0.8 },
  });
  const titleOpacity = interpolate(frame, [0, 18], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(titleProgress, [0, 1], [-30, 0]);

  const PHOTO_W = 240;
  const PHOTO_H = 280;

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at center, #fdf6ec 0%, #f0e4d0 65%, #e6d5b8 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 桌面纹理（微噪点，用 CSS 渐变层营造木纹/纸质感） */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(115deg, rgba(120,80,40,0.04) 0px, rgba(120,80,40,0.04) 2px, transparent 2px, transparent 10px)',
          pointerEvents: 'none',
        }}
      />

      {/* 标题区 */}
        <div
          style={{
            position: 'absolute',
            top: 15,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
            zIndex: 100,
          }}
        >
          <div
            style={{
              fontFamily: '"Brush Script MT", "Segoe Script", cursive',
              fontSize: 58,
              color: '#3d2a1a',
              letterSpacing: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.12)',
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontFamily: '"Georgia", serif',
              fontSize: 22,
              color: '#7a5a3a',
              letterSpacing: 4,
              marginTop: 4,
              fontStyle: 'italic',
            }}
          >
            {subtitle}
          </div>
        </div>

        {/* 照片墙 */}
        {photos.map((photo, i) => {
          const enter = spring({
            frame: frame - photo.delay,
            fps,
            config: { damping: 14, stiffness: 90, mass: 0.7 },
          });

          // 入场：从上方落下 + 轻微过冲旋转
          const dropY = interpolate(enter, [0, 1], [-200, 0]);
          const entryRot = interpolate(enter, [0, 1], [photo.rot - 25, photo.rot]);
          const opacity = interpolate(frame - photo.delay, [0, 10], [0, 1], {
            extrapolateRight: 'clamp',
          });

          // 入场后：轻微漂浮呼吸（相位错开，避免整体同步）
          const idleFrame = Math.max(0, frame - photo.delay - 30);
          const floatY = Math.sin((idleFrame / fps) * 1.2 + i) * 2.5;
          const floatRot = Math.sin((idleFrame / fps) * 0.9 + i * 0.7) * 0.4;

          const left = photo.cx - PHOTO_W / 2 - 30;
          const top = photo.cy - PHOTO_H / 2;

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left,
                top,
                width: PHOTO_W,
                height: PHOTO_H,
                backgroundColor: '#fffdf7',
                padding: '14px 14px 42px 14px',
                transform: `translateY(${dropY + floatY}px) rotate(${
                  entryRot + floatRot
                }deg)`,
                opacity,
                boxShadow:
                  '0 12px 28px rgba(60,40,20,0.28), 0 2px 6px rgba(60,40,20,0.18)',
                zIndex: photo.z,
                transformOrigin: 'center center',
              }}
            >
              {/* 照片区 */}
              <div
                style={{
                  width: '100%',
                  height: PHOTO_H - 14 - 42,
                  overflow: 'hidden',
                  backgroundColor: '#000',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
                }}
              >
                <img
                  src={photo.image}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(0.92) contrast(1.02)',
                  }}
                />
              </div>
              {/* 手写注解 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 8,
                  textAlign: 'center',
                  fontFamily: '"Caveat", "Brush Script MT", cursive',
                  fontSize: 22,
                  color: '#4a3525',
                  letterSpacing: 1,
                }}
              >
                {photo.caption}
              </div>
              {/* 左上角胶带装饰 */}
              <div
                style={{
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: `translateX(-50%) rotate(${i % 2 === 0 ? -6 : 6}deg)`,
                  width: 70,
                  height: 22,
                  background:
                    'linear-gradient(180deg, rgba(255,220,140,0.75) 0%, rgba(255,200,100,0.55) 100%)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                  borderLeft: '1px dashed rgba(120,90,30,0.25)',
                  borderRight: '1px dashed rgba(120,90,30,0.25)',
                }}
              />
            </div>
          );
        })}
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #123 双层电影胶片传送带（ImageFilmStripReel）
// 设计思路：
// 1. 经典 35mm 胶片的穿孔 / 齿孔视觉语言，上下两条相反方向滚动的胶片带
// 2. 画面中央"NOW PLAYING"放映窗口，聚光灯高亮并定时切换下一张
// 3. 顶部放映厅信息牌 + 底部胶片时间码条，整体复古工业风
// 4. 全程连续无缝滚动，带入场侧滑 + 呼吸聚光
const FILM_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/film-1/600/400',
  'https://picsum.photos/seed/film-2/600/400',
  'https://picsum.photos/seed/film-3/600/400',
  'https://picsum.photos/seed/film-4/600/400',
  'https://picsum.photos/seed/film-5/600/400',
  'https://picsum.photos/seed/film-6/600/400',
  'https://picsum.photos/seed/film-7/600/400',
  'https://picsum.photos/seed/film-8/600/400',
];

const FILM_DEFAULT_CAPTIONS = [
  'Overture',
  'Monochrome',
  'Interlude',
  'Nocturne',
  'Aperture',
  'Reverie',
  'Fade Out',
  'Encore',
];

export const ImageFilmStripReel: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = FILM_DEFAULT_IMAGES,
  captions = FILM_DEFAULT_CAPTIONS,
  title = 'CINÉMATHÈQUE',
  subtitle = 'Reel No. XXVI · Eight Short Stories',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 胶片单元尺寸（画面 + 底部注释）
  const FRAME_W = 210;
  const FRAME_H = 140;
  const FRAME_GAP = 14;
  const STRIP_STEP = FRAME_W + FRAME_GAP;
  const STRIP_H = FRAME_H + 44; // 含齿孔边
  const count = images.length;
  const loopLen = STRIP_STEP * count;

  // 入场：整条胶片从两侧滑入 + 标题淡入
  const enter = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 90, mass: 0.9 },
  });
  const titleOpacity = interpolate(frame, [0, 22], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const slideIn = interpolate(enter, [0, 1], [220, 0]);

  // 上下两条胶片方向相反、速度相近
  const speedTop = 2.4; // px / frame
  const speedBottom = 2.0;
  const offsetTop = (frame * speedTop) % loopLen;
  const offsetBottom = (frame * speedBottom) % loopLen;

  // 中央放映窗口：每 60 帧切换 "正在放映"
  const playInterval = 60;
  const playIndex = Math.floor(frame / playInterval) % count;
  const playProgress = (frame % playInterval) / playInterval;
  // 放映窗口缓慢呼吸 + 切换时从 0.95→1 过冲
  const breath = 1 + Math.sin((frame / fps) * 2.4) * 0.012;
  const switchScale = interpolate(playProgress, [0, 0.15, 1], [0.95, 1.02, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const switchOpacity = interpolate(playProgress, [0, 0.15, 1], [0.4, 1, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 放映窗口尺寸
  const MARQUEE_W = 560;
  const MARQUEE_H = 320;
  const marqueeX = (width - MARQUEE_W) / 2;
  const marqueeY = (height - MARQUEE_H) / 2 - 10;

  const renderStrip = (reverse: boolean, shift: number, y: number) => {
    // 额外复制一份图片保证无缝循环
    const stripImages = [...images, ...images];
    const direction = reverse ? -1 : 1;
    return (
      <div
        style={{
          position: 'absolute',
          top: y,
          left: 0,
          width: '100%',
          height: STRIP_H,
          overflow: 'hidden',
          background: '#0a0a0a',
          boxShadow:
            '0 4px 18px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.04)',
          transform: `translateX(${direction * slideIn}px)`,
        }}
      >
        {/* 顶部齿孔条 */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 14,
            background:
              'repeating-linear-gradient(to right, #e8e2cc 0px, #e8e2cc 12px, #111 12px, #111 24px)',
            opacity: 0.88,
          }}
        />
        {/* 底部齿孔条 */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 14,
            background:
              'repeating-linear-gradient(to right, #e8e2cc 0px, #e8e2cc 12px, #111 12px, #111 24px)',
            opacity: 0.88,
          }}
        />

        {/* 胶片格 */}
        <div
          style={{
            position: 'absolute',
            top: 14,
            height: FRAME_H + 16,
            left: 0,
            width: loopLen * 2,
            transform: `translateX(${direction * -shift}px)`,
          }}
        >
          {stripImages.map((image, i) => {
            const cap = captions[i % captions.length];
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: i * STRIP_STEP,
                  top: 0,
                  width: FRAME_W,
                  height: FRAME_H + 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 4,
                }}
              >
                <div
                  style={{
                    width: FRAME_W,
                    height: FRAME_H,
                    overflow: 'hidden',
                    background: '#000',
                    boxShadow:
                      '0 0 0 2px #1c1c1c, inset 0 0 0 1px rgba(255,255,255,0.08)',
                  }}
                >
                  <Img
                    src={image}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'saturate(0.85) contrast(1.08) brightness(0.92)',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontFamily:
                      '"SF Mono", "Menlo", "Courier New", monospace',
                    fontSize: 9,
                    letterSpacing: 3,
                    color: '#c9b489',
                    textTransform: 'uppercase',
                    opacity: 0.78,
                  }}
                >
                  {String((i % count) + 1).padStart(2, '0')} · {cap}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 50%, #1a1a1a 0%, #0c0c0c 55%, #030303 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景放映机微粒 / 光束（固定位置，保证可复现） */}
      {Array.from({ length: 28 }).map((_, i) => {
        const seed = (i * 131) % 997;
        const px = (seed * 13) % width;
        const py = 60 + ((seed * 29) % (height - 120));
        const life = (frame + seed) % 90;
        const opacity = Math.sin((life / 90) * Math.PI) * 0.35;
        return (
          <div
            key={`dust-${i}`}
            style={{
              position: 'absolute',
              left: px,
              top: py,
              width: 2,
              height: 2,
              borderRadius: '50%',
              background: '#f4e6c3',
              opacity,
              boxShadow: '0 0 6px rgba(244,230,195,0.6)',
            }}
          />
        );
      })}

      {/* 顶部：放映厅标识牌 */}
      <div
        style={{
          position: 'absolute',
          top: 26,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          opacity: titleOpacity,
          zIndex: 30,
        }}
      >
        <div
          style={{
            padding: '6px 22px',
            border: '1px solid rgba(244,230,195,0.4)',
            borderRadius: 2,
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 11,
            letterSpacing: 8,
            color: '#c9b489',
            textTransform: 'uppercase',
            background: 'rgba(0,0,0,0.45)',
          }}
        >
          Reel Room · 07
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: 10,
            color: '#f4e6c3',
            textShadow: '0 0 14px rgba(244,230,195,0.25)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: 2,
            fontFamily: '"Georgia", serif',
            fontSize: 11,
            letterSpacing: 5,
            color: '#8b7a55',
            fontStyle: 'italic',
            textTransform: 'uppercase',
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 上胶片（靠上方；从右往左滚动） */}
      {renderStrip(true, offsetTop, 120)}

      {/* 下胶片（靠下方；从左往右滚动） */}
      {renderStrip(false, offsetBottom, height - STRIP_H - 46)}

      {/* 中央 "NOW PLAYING" 放映窗口 */}
      <div
        style={{
          position: 'absolute',
          left: marqueeX,
          top: marqueeY,
          width: MARQUEE_W,
          height: MARQUEE_H,
          transform: `scale(${switchScale * breath})`,
          transformOrigin: 'center center',
          zIndex: 20,
          filter: 'drop-shadow(0 18px 44px rgba(0,0,0,0.75))',
        }}
      >
        {/* 聚光灯背光 */}
        <div
          style={{
            position: 'absolute',
            inset: -90,
            background:
              'radial-gradient(ellipse at center, rgba(255,230,170,0.28) 0%, rgba(255,230,170,0.08) 40%, transparent 70%)',
            filter: 'blur(6px)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: 10,
            background: 'linear-gradient(180deg, #1d1a14 0%, #0d0b08 100%)',
            border: '1px solid rgba(244,230,195,0.45)',
            borderRadius: 6,
            boxShadow:
              '0 0 0 8px rgba(0,0,0,0.55), 0 0 30px rgba(244,230,195,0.15) inset',
          }}
        >
          {/* 画面区 */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '78%',
              overflow: 'hidden',
              background: '#000',
              boxShadow:
                'inset 0 0 0 1px rgba(244,230,195,0.3), inset 0 0 60px rgba(0,0,0,0.6)',
            }}
          >
            <Img
              key={`play-${playIndex}`}
              src={images[playIndex]}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: switchOpacity,
                filter: 'saturate(1.05) contrast(1.05)',
              }}
            />
            {/* 胶片颗粒 + 扫描线 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 3px)',
                mixBlendMode: 'overlay',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)',
                pointerEvents: 'none',
              }}
            />
            {/* 左上 REC 标 */}
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: '"SF Mono", monospace',
                fontSize: 10,
                letterSpacing: 3,
                color: '#f4e6c3',
                opacity: 0.8,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#e63946',
                  boxShadow: '0 0 8px rgba(230,57,70,0.9)',
                  opacity: 0.6 + (Math.sin(frame / 6) + 1) / 5,
                }}
              />
              REC
            </div>
            {/* 右上时间码 */}
            <div
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                fontFamily: '"SF Mono", monospace',
                fontSize: 10,
                letterSpacing: 2,
                color: '#c9b489',
                opacity: 0.8,
              }}
            >
              {`00:${String(Math.floor(frame / fps) % 60).padStart(2, '0')}:${String(frame % fps).padStart(2, '0')}`}
            </div>
          </div>

          {/* 标签区 */}
          <div
            style={{
              height: '22%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 14px',
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
              <div
                style={{
                  fontFamily: '"SF Mono", monospace',
                  fontSize: 10,
                  letterSpacing: 4,
                  color: '#8b7a55',
                  textTransform: 'uppercase',
                }}
              >
                Now Playing
              </div>
              <div
                key={`cap-${playIndex}`}
                style={{
                  fontFamily: '"Georgia", serif',
                  fontSize: 24,
                  letterSpacing: 2,
                  color: '#f4e6c3',
                  fontStyle: 'italic',
                  textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                }}
              >
                {captions[playIndex % captions.length]}
              </div>
            </div>
            <div
              style={{
                textAlign: 'right',
                fontFamily: '"SF Mono", monospace',
                fontSize: 10,
                letterSpacing: 3,
                color: '#c9b489',
              }}
            >
              <div>{`CHAPTER ${String(playIndex + 1).padStart(2, '0')}/${String(count).padStart(2, '0')}`}</div>
              <div style={{opacity: 0.6, marginTop: 2}}>35mm · 24fps</div>
            </div>
          </div>
        </div>

        {/* 聚光灯光锥（从上方投下） */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: -180,
            transform: 'translateX(-50%)',
            width: 240,
            height: 200,
            background:
              'linear-gradient(180deg, rgba(255,230,170,0.0) 0%, rgba(255,230,170,0.18) 60%, rgba(255,230,170,0) 100%)',
            clipPath: 'polygon(35% 0%, 65% 0%, 100% 100%, 0% 100%)',
            filter: 'blur(2px)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* 底部：时间码条 + 放映机状态 */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 28,
          fontFamily: '"SF Mono", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#8b7a55',
          textTransform: 'uppercase',
          opacity: titleOpacity,
          zIndex: 30,
        }}
      >
        <span>MMXXVI</span>
        <span style={{opacity: 0.55}}>·</span>
        <span>Dolby · Projected at 24fps</span>
        <span style={{opacity: 0.55}}>·</span>
        <span>{`Reel A/${String(playIndex + 1).padStart(2, '0')}`}</span>
      </div>
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #124 六边形蜂窝拼图（ImageHexagonHoneycomb）
// 设计思路：
// 1. 使用 clip-path 把图片裁剪为正六边形，天然打破矩形网格的千篇一律
// 2. 中心 + 6 片外圈蜂窝结构，并在两侧附加补充蜂窝形成层次
// 3. 入场从中心向外环形波纹展开（依据距离中心的环号 + 角度延迟）
// 4. 中央六边形以金色描边强调，呼吸发光；其余六边形带编号与标签
// 5. 背景为深墨色 + 细六边网格纹理，极客展厅感
const HEX_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/hex-1/600/600',
  'https://picsum.photos/seed/hex-2/600/600',
  'https://picsum.photos/seed/hex-3/600/600',
  'https://picsum.photos/seed/hex-4/600/600',
  'https://picsum.photos/seed/hex-5/600/600',
  'https://picsum.photos/seed/hex-6/600/600',
  'https://picsum.photos/seed/hex-7/600/600',
  'https://picsum.photos/seed/hex-8/600/600',
  'https://picsum.photos/seed/hex-9/600/600',
];

const HEX_DEFAULT_CAPTIONS = [
  'Core',
  'Dawn',
  'Bloom',
  'Tide',
  'Drift',
  'Glow',
  'Echo',
  'Muse',
  'Nova',
];

export const ImageHexagonHoneycomb: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = HEX_DEFAULT_IMAGES,
  captions = HEX_DEFAULT_CAPTIONS,
  title = 'HEX · COMB',
  subtitle = 'Nine Cells of Light · A Hexagonal Atlas',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 尖顶六边形（pointy-top）：宽 W，高 H = W * 2/sqrt(3)
  const HEX_W = 200;
  const HEX_H = HEX_W * 1.1547; // 2/sqrt(3) ≈ 1.1547
  // 相邻六边形偏移
  // pointy-top 蜂窝：水平邻居 dx=W, 垂直对角邻居 dx=W/2, dy=0.75*H
  const dxHalf = HEX_W / 2;
  const dyStep = HEX_H * 0.75;

  // 中心 + 6 片第一圈 + 2 片第二圈（总计 9，配套 9 张图）
  // 坐标单位：以中心为 (0,0)
  // 第一圈 6 方向（pointy-top）：N,NE,SE,S,SW,NW
  // 但对于 pointy-top 的经典 6 邻居：
  //   E:(W,0), W:(-W,0), NE:(W/2,-0.75H), NW:(-W/2,-0.75H), SE:(W/2,0.75H), SW:(-W/2,0.75H)
  const cells: Array<{x: number; y: number; ring: number; angle: number}> = [
    // ring 0
    { x: 0, y: 0, ring: 0, angle: 0 },
    // ring 1 - 6 cells
    { x: HEX_W, y: 0, ring: 1, angle: 0 }, // E
    { x: dxHalf, y: -dyStep, ring: 1, angle: 60 }, // NE
    { x: -dxHalf, y: -dyStep, ring: 1, angle: 120 }, // NW
    { x: -HEX_W, y: 0, ring: 1, angle: 180 }, // W
    { x: -dxHalf, y: dyStep, ring: 1, angle: 240 }, // SW
    { x: dxHalf, y: dyStep, ring: 1, angle: 300 }, // SE
    // ring 2 - 2 额外的"翅膀"，让整体更宽展
    { x: HEX_W * 2, y: 0, ring: 2, angle: 0 }, // 最右
    { x: -HEX_W * 2, y: 0, ring: 2, angle: 180 }, // 最左
  ];

  // 画面中心
  const headerHeight = 104;
  const cx = width / 2;
  const cy = headerHeight + (height - headerHeight) / 2 + 6;

  // 标题入场
  const titleEnter = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140, mass: 0.8 },
  });
  const titleOpacity = interpolate(frame, [0, 22], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(titleEnter, [0, 1], [-22, 0]);
  const lineWidth = interpolate(titleEnter, [0, 1], [0, 180]);

  // 六边形 clip-path（尖顶）
  const hexClip =
    'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 55%, #1a2240 0%, #0c1128 55%, #04050f 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景六边纹理（静态静谧） */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(60deg, rgba(244,230,195,0.018) 0 1px, transparent 1px 20px),repeating-linear-gradient(-60deg, rgba(244,230,195,0.018) 0 1px, transparent 1px 20px),repeating-linear-gradient(0deg, rgba(244,230,195,0.018) 0 1px, transparent 1px 20px)',
          pointerEvents: 'none',
        }}
      />

      {/* 缓慢旋转光晕 */}
      <div
        style={{
          position: 'absolute',
          left: cx - 360,
          top: cy - 360,
          width: 720,
          height: 720,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(244,230,195,0.09) 0%, rgba(244,230,195,0) 65%)',
          transform: `rotate(${frame * 0.3}deg)`,
          pointerEvents: 'none',
        }}
      />

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 18,
          left: 0,
          right: 0,
          height: headerHeight - 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 34,
            fontWeight: 700,
            color: '#f4e6c3',
            letterSpacing: 10,
            textShadow: '0 0 16px rgba(244,230,195,0.28)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginTop: 6,
          }}
        >
          <div
            style={{
              width: lineWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
          <div
            style={{
              fontFamily: '"Georgia", serif',
              fontSize: 12,
              color: '#c9b489',
              letterSpacing: 5,
              textTransform: 'uppercase',
              fontStyle: 'italic',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
          <div
            style={{
              width: lineWidth,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(244,230,195,0.85), transparent)',
            }}
          />
        </div>
      </div>

      {/* 蜂窝容器 */}
      <div
        style={{
          position: 'absolute',
          left: cx,
          top: cy,
          width: 0,
          height: 0,
        }}
      >
        {cells.map((cell, i) => {
          const image = images[i % images.length];
          const caption = captions[i % captions.length];
          const isCenter = cell.ring === 0;

          // 入场：先中心→第一圈按角度→第二圈
          // 每圈 baseDelay + 圈内按角度的微小错位
          const ringDelay = cell.ring * 10;
          const angleDelay = (cell.angle / 60) * 2.5;
          const delay = 6 + ringDelay + angleDelay;

          const enter = spring({
            frame: frame - delay,
            fps,
            config: { damping: 15, stiffness: 120, mass: 0.7 },
          });
          const opacity = interpolate(frame - delay, [0, 14], [0, 1], {
            extrapolateRight: 'clamp',
          });
          // 波纹：从中心向外（从内向外放大入场）
          const scale = interpolate(enter, [0, 1], [0.4, 1]);
          const rot = interpolate(enter, [0, 1], [30, 0]);

          // 入场后：轻微错位呼吸
          const idle = Math.max(0, frame - delay - 22);
          const floatY = Math.sin((idle / fps) * 1.2 + i * 0.9) * 2;

          // 中心单元轻微脉动
          const pulse = isCenter
            ? 1 + Math.sin((idle / fps) * 2.0) * 0.018
            : 1;

          // 标签 / 编号延迟出现
          const labelOpacity = interpolate(
            frame - delay - 10,
            [0, 14],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: cell.x - HEX_W / 2,
                top: cell.y - HEX_H / 2 + floatY,
                width: HEX_W,
                height: HEX_H,
                opacity,
                transform: `scale(${scale * pulse}) rotate(${rot}deg)`,
                transformOrigin: 'center center',
              }}
            >
              {/* 金色外六边描边：用稍大的一层 div，其 clipPath 同形 */}
              <div
                style={{
                  position: 'absolute',
                  inset: -3,
                  background: isCenter
                    ? 'linear-gradient(135deg, #f4e6c3 0%, #c9a65a 50%, #f4e6c3 100%)'
                    : 'linear-gradient(135deg, rgba(244,230,195,0.55) 0%, rgba(201,180,137,0.3) 100%)',
                  clipPath: hexClip,
                  filter: isCenter
                    ? 'drop-shadow(0 0 14px rgba(244,230,195,0.45))'
                    : 'drop-shadow(0 6px 14px rgba(0,0,0,0.5))',
                }}
              />

              {/* 图片本体（内六边形） */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  clipPath: hexClip,
                  background: '#000',
                  overflow: 'hidden',
                }}
              >
                <Img
                  src={image}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: isCenter
                      ? 'saturate(1.15) contrast(1.08)'
                      : 'saturate(0.95) contrast(1.02) brightness(0.88)',
                  }}
                />
                {/* 顶部微光 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 45%, rgba(0,0,0,0.35) 100%)',
                    pointerEvents: 'none',
                  }}
                />
                {/* 底部标签 */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: '14%',
                    textAlign: 'center',
                    opacity: labelOpacity,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      fontFamily: '"SF Mono", monospace',
                      fontSize: isCenter ? 11 : 9,
                      letterSpacing: 4,
                      color: isCenter ? '#f4e6c3' : '#c9b489',
                      textTransform: 'uppercase',
                      opacity: 0.9,
                    }}
                  >
                    №{String(i + 1).padStart(2, '0')}
                  </div>
                  <div
                    style={{
                      fontFamily: '"Georgia", serif',
                      fontStyle: 'italic',
                      fontSize: isCenter ? 20 : 14,
                      letterSpacing: 2,
                      color: '#f4e6c3',
                      textShadow: '0 2px 6px rgba(0,0,0,0.75)',
                      marginTop: 2,
                    }}
                  >
                    {caption}
                  </div>
                </div>

                {/* 中心：额外内金边 */}
                {isCenter && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 12,
                      clipPath: hexClip,
                      background:
                        'linear-gradient(135deg, rgba(244,230,195,0.6) 0%, rgba(244,230,195,0) 60%)',
                      mixBlendMode: 'overlay',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* 中心与第一圈之间的连线微光（6 条短线，指向中心） */}
        {[0, 60, 120, 180, 240, 300].map((deg, i) => {
          const delay = 18 + i * 1.5;
          const op = interpolate(frame - delay, [0, 20], [0, 0.5], {
            extrapolateRight: 'clamp',
          });
          return (
            <div
              key={`spoke-${i}`}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: HEX_W * 0.9,
                height: 1,
                background:
                  'linear-gradient(90deg, rgba(244,230,195,0.7), rgba(244,230,195,0))',
                transform: `rotate(${deg}deg) translateX(${HEX_W * 0.35}px)`,
                transformOrigin: '0 0',
                opacity: op,
                pointerEvents: 'none',
              }}
            />
          );
        })}
      </div>

      {/* 底部页脚 */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          fontFamily: '"SF Mono", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#8b7a55',
          textTransform: 'uppercase',
          opacity: titleOpacity,
        }}
      >
        <span>Comb Index · 09 Cells</span>
        <span style={{opacity: 0.55}}>·</span>
        <span>Radial Bloom Sequence</span>
      </div>

      {/* 边缘暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #125 杂志对开页（ImageMagazineSpread）
// 设计思路：
// 1. 模拟一本翻开的杂志：左页大图 + 右页栏位（小图网格 + 模拟正文）
// 2. 3D 翻开入场：整本杂志起始为 rotateY(90deg)，像书本被掀开
// 3. 右页 2x2 小图按波浪依次入场；正文栏线条自上而下依次显现
// 4. 左页带装饰刊名 / 期号 / 页码；右页带首字大写下沉、作者署名
// 5. 中间书脊具有内阴影与高光，模拟书本弯曲质感
const MAG_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/mag-hero/1000/1400',
  'https://picsum.photos/seed/mag-a/600/600',
  'https://picsum.photos/seed/mag-b/600/600',
  'https://picsum.photos/seed/mag-c/600/600',
  'https://picsum.photos/seed/mag-d/600/600',
];

const MAG_DEFAULT_CAPTIONS = [
  'Chasing Quiet Hours',
  'Objects',
  'Rituals',
  'Rooms',
  'Letters',
];

export const ImageMagazineSpread: React.FC<{
  images?: string[];
  captions?: string[];
  issueTitle?: string;
  articleTitle?: string;
  articleLead?: string;
  author?: string;
}> = ({
  images = MAG_DEFAULT_IMAGES,
  captions = MAG_DEFAULT_CAPTIONS,
  issueTitle = 'MOMENT',
  articleTitle = 'Quiet Hours',
  articleLead = 'An essay on the small light that lingers between afternoons — a study of stillness, of what stays when the noise is gone.',
  author = 'by Ellis Wren',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 整体翻开入场
  const openProgress = spring({
    frame,
    fps,
    config: { damping: 22, stiffness: 55, mass: 1.1 },
  });
  const rotY = interpolate(openProgress, [0, 1], [72, 0]);
  const opacity = interpolate(frame, [0, 18], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 杂志整体：留出上下边距
  const MARGIN_X = 36;
  const MARGIN_Y = 40;
  const spreadW = width - MARGIN_X * 2;
  const spreadH = height - MARGIN_Y * 2;
  const pageW = spreadW / 2;

  // 右页小图 2x2
  const smallImages = images.slice(1, 5);
  const smallCaptions = captions.slice(1, 5);

  // 假正文行数（等宽横线）
  const TEXT_LINES = 12;

  const heroImage = images[0];

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at center, #3c2c1c 0%, #241812 55%, #130b08 100%)',
        overflow: 'hidden',
        perspective: 1800,
      }}
    >
      <ScaledStage>
      {/* 桌面木纹纹理 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(92deg, rgba(0,0,0,0.12) 0 1px, transparent 1px 6px),repeating-linear-gradient(2deg, rgba(120,80,40,0.06) 0 1px, transparent 1px 14px)',
          pointerEvents: 'none',
        }}
      />

      {/* 杂志对开页容器 */}
      <div
        style={{
          position: 'absolute',
          left: MARGIN_X,
          top: MARGIN_Y,
          width: spreadW,
          height: spreadH,
          transformStyle: 'preserve-3d',
          transform: `rotateX(6deg) rotateY(${rotY}deg)`,
          transformOrigin: '50% 100%',
          opacity,
          filter: 'drop-shadow(0 24px 50px rgba(0,0,0,0.7))',
        }}
      >
        {/* 底部桌面投影 */}
        <div
          style={{
            position: 'absolute',
            left: -20,
            right: -20,
            bottom: -30,
            height: 40,
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)',
            filter: 'blur(8px)',
            transform: 'translateZ(-10px)',
          }}
        />

        {/* ===== 左页 ===== */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: pageW,
            height: spreadH,
            background:
              'linear-gradient(90deg, #f6efe1 0%, #efe5d1 94%, #cfc2a8 100%)',
            boxShadow: 'inset -18px 0 24px rgba(90,60,30,0.25)',
            padding: '26px 26px 26px 32px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* 刊头 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              borderBottom: '1px solid rgba(60,40,20,0.35)',
              paddingBottom: 10,
            }}
          >
            <div
              style={{
                fontFamily: '"Georgia", "Times New Roman", serif',
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: 8,
                color: '#2d1f12',
              }}
            >
              {issueTitle}
            </div>
            <div
              style={{
                fontFamily: '"Georgia", serif',
                fontSize: 10,
                letterSpacing: 4,
                color: '#6b4e30',
                textTransform: 'uppercase',
              }}
            >
              Vol. XXVI · Spring Issue
            </div>
          </div>

          {/* 大主图 */}
          <div
            style={{
              flex: 1,
              marginTop: 14,
              overflow: 'hidden',
              background: '#000',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
              position: 'relative',
            }}
          >
            <Img
              src={heroImage}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(0.95) contrast(1.05)',
                transform: `scale(${1 + (1 - openProgress) * 0.06})`,
              }}
            />
            {/* 画面底部柔光 */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: '40%',
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%)',
              }}
            />
            {/* 图片标题（压在图上） */}
            <div
              style={{
                position: 'absolute',
                left: 22,
                bottom: 18,
                right: 22,
                color: '#f9f1df',
                opacity: interpolate(frame - 20, [0, 20], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                }),
              }}
            >
              <div
                style={{
                  fontFamily: '"Georgia", serif',
                  fontSize: 11,
                  letterSpacing: 6,
                  textTransform: 'uppercase',
                  opacity: 0.85,
                }}
              >
                Feature · 04
              </div>
              <div
                style={{
                  fontFamily: '"Georgia", serif',
                  fontSize: 36,
                  fontStyle: 'italic',
                  fontWeight: 400,
                  letterSpacing: 1,
                  marginTop: 4,
                  textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                }}
              >
                {articleTitle}
              </div>
            </div>
          </div>

          {/* 页脚：页码 + 栏目 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
              fontFamily: '"Georgia", serif',
              fontSize: 10,
              letterSpacing: 4,
              color: '#6b4e30',
              textTransform: 'uppercase',
            }}
          >
            <span>— 24 —</span>
            <span>{captions[0]}</span>
          </div>
        </div>

        {/* ===== 右页 ===== */}
        <div
          style={{
            position: 'absolute',
            left: pageW,
            top: 0,
            width: pageW,
            height: spreadH,
            background:
              'linear-gradient(270deg, #f6efe1 0%, #efe5d1 94%, #cfc2a8 100%)',
            boxShadow: 'inset 18px 0 24px rgba(90,60,30,0.25)',
            padding: '26px 32px 26px 26px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* 页眉 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              borderBottom: '1px solid rgba(60,40,20,0.35)',
              paddingBottom: 10,
            }}
          >
            <div
              style={{
                fontFamily: '"Georgia", serif',
                fontSize: 10,
                letterSpacing: 4,
                color: '#6b4e30',
                textTransform: 'uppercase',
              }}
            >
              Essay · pp. 25
            </div>
            <div
              style={{
                fontFamily: '"Georgia", serif',
                fontSize: 10,
                letterSpacing: 4,
                color: '#6b4e30',
                textTransform: 'uppercase',
                fontStyle: 'italic',
              }}
            >
              {author}
            </div>
          </div>

          {/* 中部：左列正文 + 右列 2x2 小图 */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              gap: 18,
              marginTop: 14,
            }}
          >
            {/* 正文列 */}
            <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
              {/* Lead / 引言 */}
              <div
                style={{
                  fontFamily: '"Georgia", serif',
                  fontStyle: 'italic',
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: '#3d2a1a',
                  opacity: interpolate(frame - 12, [0, 16], [0, 1], {
                    extrapolateLeft: 'clamp',
                    extrapolateRight: 'clamp',
                  }),
                }}
              >
                <span
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 46,
                    fontStyle: 'normal',
                    fontWeight: 700,
                    float: 'left',
                    lineHeight: 0.95,
                    marginRight: 6,
                    marginTop: 2,
                    color: '#2d1f12',
                  }}
                >
                  {articleLead.charAt(0)}
                </span>
                {articleLead.slice(1)}
              </div>

              {/* 模拟正文行（波浪淡入） */}
              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 7,
                  flex: 1,
                }}
              >
                {Array.from({ length: TEXT_LINES }).map((_, li) => {
                  const lineDelay = 22 + li * 2.5;
                  const lineOp = interpolate(
                    frame - lineDelay,
                    [0, 14],
                    [0, 1],
                    {
                      extrapolateLeft: 'clamp',
                      extrapolateRight: 'clamp',
                    }
                  );
                  const lineX = interpolate(
                    frame - lineDelay,
                    [0, 16],
                    [-8, 0],
                    {
                      extrapolateLeft: 'clamp',
                      extrapolateRight: 'clamp',
                    }
                  );
                  // 长度交替：模拟真实段落节奏
                  const widths = [
                    '96%', '92%', '88%', '95%', '70%',
                    '94%', '89%', '92%', '76%', '97%',
                    '93%', '58%',
                  ];
                  const isParaEnd = li === 4 || li === 8;
                  return (
                    <div
                      key={li}
                      style={{
                        width: widths[li],
                        height: 3,
                        background:
                          'linear-gradient(90deg, rgba(60,40,20,0.7), rgba(60,40,20,0.35))',
                        borderRadius: 1,
                        opacity: lineOp * (isParaEnd ? 0.6 : 1),
                        transform: `translateX(${lineX}px)`,
                        marginTop: isParaEnd ? 6 : 0,
                      }}
                    />
                  );
                })}
              </div>

              {/* 署名 */}
              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  opacity: interpolate(frame - 70, [0, 16], [0, 1], {
                    extrapolateLeft: 'clamp',
                    extrapolateRight: 'clamp',
                  }),
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 1,
                    background: 'rgba(60,40,20,0.5)',
                  }}
                />
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 11,
                    letterSpacing: 4,
                    color: '#6b4e30',
                    textTransform: 'uppercase',
                    fontStyle: 'italic',
                  }}
                >
                  To be continued ·  p. 26
                </div>
              </div>
            </div>

            {/* 小图 2x2 */}
            <div
              style={{
                width: 240,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridTemplateRows: 'repeat(2, 1fr)',
                gap: 8,
              }}
            >
              {smallImages.map((img, i) => {
                const delay = 30 + i * 6;
                const e = spring({
                  frame: frame - delay,
                  fps,
                  config: { damping: 14, stiffness: 110, mass: 0.7 },
                });
                const op = interpolate(frame - delay, [0, 14], [0, 1], {
                  extrapolateRight: 'clamp',
                });
                const s = interpolate(e, [0, 1], [0.88, 1]);
                const ty = interpolate(e, [0, 1], [14, 0]);
                return (
                  <div
                    key={i}
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      background: '#000',
                      opacity: op,
                      transform: `translateY(${ty}px) scale(${s})`,
                      boxShadow:
                        '0 4px 10px rgba(60,40,20,0.25), inset 0 0 0 1px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Img
                      src={img}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'saturate(0.92) contrast(1.04)',
                      }}
                    />
                    {/* 编号 */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 6,
                        fontFamily: '"SF Mono", monospace',
                        fontSize: 8,
                        letterSpacing: 2,
                        color: '#f9f1df',
                        opacity: 0.9,
                        textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                      }}
                    >
                      FIG {String(i + 1).padStart(2, '0')}
                    </div>
                    {/* 底部标签 */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: '8px 8px 6px',
                        background:
                          'linear-gradient(180deg, transparent, rgba(0,0,0,0.75))',
                        color: '#f9f1df',
                        fontFamily: '"Georgia", serif',
                        fontStyle: 'italic',
                        fontSize: 11,
                        letterSpacing: 0.5,
                      }}
                    >
                      {smallCaptions[i]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 页脚：页码 + 刊名 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
              fontFamily: '"Georgia", serif',
              fontSize: 10,
              letterSpacing: 4,
              color: '#6b4e30',
              textTransform: 'uppercase',
            }}
          >
            <span>{issueTitle} · Vol. XXVI</span>
            <span>— 25 —</span>
          </div>
        </div>

        {/* ===== 中心书脊 ===== */}
        <div
          style={{
            position: 'absolute',
            left: pageW - 12,
            top: 0,
            width: 24,
            height: spreadH,
            background:
              'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 25%, rgba(0,0,0,0.78) 50%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0) 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* 书脊上端/下端微高光 */}
        <div
          style={{
            position: 'absolute',
            left: pageW - 1,
            top: 0,
            width: 2,
            height: spreadH,
            background:
              'linear-gradient(180deg, rgba(255,240,210,0.3) 0%, rgba(255,240,210,0) 20%, rgba(255,240,210,0) 80%, rgba(255,240,210,0.3) 100%)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* 左下角水印 */}
      <div
        style={{
          position: 'absolute',
          bottom: 14,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: '"Georgia", serif',
          fontSize: 10,
          letterSpacing: 5,
          color: 'rgba(244,230,195,0.4)',
          textTransform: 'uppercase',
          opacity,
        }}
      >
        {issueTitle} · An Editorial Spread · MMXXVI
      </div>
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #126 调查板 · 软木钉板（ImagePolaroidCorkBoard）
// 设计思路：
// 1. 复刻"犯罪调查 / 灵感 mood board"的视觉语言：软木纹理底板 + 图钉 + 红色毛线
// 2. 5 张拍立得照片以自然错落的姿态被图钉定住，彼此间由红线相连，形成"证据链"
// 3. 额外的便利贴 / 纸条提供手写注解，胶带固定照片一角，增加真实手感
// 4. 动画：图钉依次按入 → 照片微弹回正 → SVG 红线按长度依次"绘出" → 便利贴浮入
// 5. 结尾加入"CASE FILE"档案头、日期戳与分类条，强化档案调查氛围
const CORK_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/case-a/640/480',
  'https://picsum.photos/seed/case-b/640/480',
  'https://picsum.photos/seed/case-c/640/480',
  'https://picsum.photos/seed/case-d/640/480',
  'https://picsum.photos/seed/case-e/640/480',
];

const CORK_DEFAULT_CAPTIONS = [
  'Subject 01',
  'Pier · 03:12',
  'The Letter',
  'Unknown Car',
  'Last Seen',
];

export const ImagePolaroidCorkBoard: React.FC<{
  images?: string[];
  captions?: string[];
  caseTitle?: string;
  caseNumber?: string;
}> = ({
  images = CORK_DEFAULT_IMAGES,
  captions = CORK_DEFAULT_CAPTIONS,
  caseTitle = 'CASE FILE · CLASSIFIED',
  caseNumber = 'NO. 2026-04-17 / K-IX',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 头部入场
  const headerOpacity = interpolate(frame, [0, 22], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 手工布局：5 张拍立得 + 位置 / 旋转（安全区：画布内 1280x720）
  const PHOTO_W = 210;
  const PHOTO_H = 250;
  const layout = [
    { cx: 260, cy: 330, rot: -7, z: 4 },
    { cx: 560, cy: 260, rot: 5, z: 6 },
    { cx: 850, cy: 340, rot: -4, z: 5 },
    { cx: 410, cy: 560, rot: 6, z: 7 },
    { cx: 1060, cy: 520, rot: -8, z: 6 },
  ];

  // 红线连接（按 layout 索引）
  const connections: Array<{ a: number; b: number; delay: number }> = [
    { a: 0, b: 1, delay: 52 },
    { a: 1, b: 2, delay: 62 },
    { a: 1, b: 3, delay: 72 },
    { a: 2, b: 4, delay: 82 },
    { a: 3, b: 4, delay: 92 },
  ];

  // 便利贴 / 纸片
  const notes = [
    {
      text: '"He was here again."',
      cx: 170,
      cy: 180,
      rot: -4,
      color: '#fff3a3',
      delay: 100,
      size: 15,
    },
    {
      text: 'CHECK TIME\n03:10 — 03:18',
      cx: 1120,
      cy: 210,
      rot: 6,
      color: '#ffd2d2',
      delay: 110,
      size: 13,
    },
    {
      text: 'witness →\nthe pier',
      cx: 730,
      cy: 560,
      rot: -5,
      color: '#bce8c0',
      delay: 120,
      size: 13,
    },
  ];

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 40%, #b3824b 0%, #7d5631 60%, #4a3418 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 软木板颗粒 / 木屑纹理：多层 repeating-gradient 模拟软木粗糙感 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-radial-gradient(circle at 20% 30%, rgba(70,40,18,0.25) 0px, rgba(70,40,18,0.25) 1px, transparent 1px, transparent 4px),repeating-radial-gradient(circle at 72% 66%, rgba(50,28,10,0.22) 0px, rgba(50,28,10,0.22) 1px, transparent 1px, transparent 5px),repeating-linear-gradient(112deg, rgba(255,220,160,0.04) 0 2px, transparent 2px 9px)',
          pointerEvents: 'none',
          mixBlendMode: 'multiply',
        }}
      />
      {/* 边缘木框暗角 */}
      <AbsoluteFill
        style={{
          boxShadow:
            'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 20px rgba(0,0,0,0.35)',
          pointerEvents: 'none',
        }}
      />

      {/* 档案头（左上角） */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: 32,
          opacity: headerOpacity,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          zIndex: 30,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '4px 12px',
            background: 'rgba(180,30,30,0.92)',
            color: '#fff6e1',
            fontFamily: '"Courier New", monospace',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 5,
            transform: 'rotate(-1.5deg)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
            width: 'fit-content',
          }}
        >
          {caseTitle}
        </div>
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 4,
            color: 'rgba(255,240,210,0.85)',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          }}
        >
          {caseNumber}
        </div>
      </div>

      {/* 右上角时间戳章 */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          right: 40,
          opacity: interpolate(frame - 18, [0, 14], [0, 0.85], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
          transform: `rotate(${-6 + interpolate(frame - 18, [0, 10], [-4, 0], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}deg)`,
          border: '3px double rgba(180,30,30,0.85)',
          padding: '6px 14px',
          color: 'rgba(180,30,30,0.9)',
          fontFamily: '"Courier New", monospace',
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 3,
          textShadow: '0 0 1px rgba(180,30,30,0.6)',
          zIndex: 30,
        }}
      >
        CONFIDENTIAL
      </div>

      {/* SVG 红线层（放在照片之下以便被钉住） */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}
      >
        {connections.map((c, i) => {
          const p1 = layout[c.a];
          const p2 = layout[c.b];
          const progress = interpolate(frame - c.delay, [0, 18], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          // 模拟绳子的轻微垂坠：在中点向下偏移 10-18px
          const midX = (p1.cx + p2.cx) / 2;
          const midY = (p1.cy + p2.cy) / 2 + 10 + ((i * 3) % 8);
          // 用二次贝塞尔逼近长度，再用 dasharray 实现"画线"
          const approxLen = Math.hypot(p2.cx - p1.cx, p2.cy - p1.cy) + 30;
          return (
            <g key={i}>
              <path
                d={`M ${p1.cx} ${p1.cy} Q ${midX} ${midY} ${p2.cx} ${p2.cy}`}
                stroke="rgba(180,30,30,0.92)"
                strokeWidth={2.2}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={approxLen}
                strokeDashoffset={approxLen * (1 - progress)}
                style={{
                  filter:
                    'drop-shadow(0 1px 1px rgba(0,0,0,0.55)) drop-shadow(0 2px 3px rgba(0,0,0,0.35))',
                }}
              />
              {/* 绳头微高光 */}
              <path
                d={`M ${p1.cx} ${p1.cy} Q ${midX} ${midY} ${p2.cx} ${p2.cy}`}
                stroke="rgba(255,170,170,0.5)"
                strokeWidth={0.6}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={approxLen}
                strokeDashoffset={approxLen * (1 - progress)}
              />
            </g>
          );
        })}
      </svg>

      {/* 拍立得照片 */}
      {layout.map((p, i) => {
        const pinDelay = 10 + i * 8;
        const photoDelay = pinDelay + 4;

        // 图钉按入
        const pinEnter = spring({
          frame: frame - pinDelay,
          fps,
          config: { damping: 14, stiffness: 260, mass: 0.5 },
        });
        const pinScale = interpolate(pinEnter, [0, 1], [0, 1]);

        // 照片入场：从图钉处"甩下"带阻尼
        const photoEnter = spring({
          frame: frame - photoDelay,
          fps,
          config: { damping: 11, stiffness: 95, mass: 0.8 },
        });
        const hangRot = interpolate(photoEnter, [0, 1], [p.rot - 18, p.rot]);
        const hangY = interpolate(photoEnter, [0, 1], [-40, 0]);
        const photoOpacity = interpolate(frame - photoDelay, [0, 12], [0, 1], {
          extrapolateRight: 'clamp',
        });

        // 入场后微呼吸
        const idle = Math.max(0, frame - photoDelay - 26);
        const idleRot = Math.sin((idle / fps) * 1.1 + i) * 0.35;
        const idleY = Math.sin((idle / fps) * 0.8 + i * 0.7) * 1.6;

        const image = images[i % images.length];
        const caption = captions[i % captions.length];

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: p.cx - PHOTO_W / 2,
              top: p.cy - PHOTO_H / 2,
              width: PHOTO_W,
              height: PHOTO_H,
              transform: `translateY(${hangY + idleY}px) rotate(${
                hangRot + idleRot
              }deg)`,
              transformOrigin: '50% 8px',
              opacity: photoOpacity,
              zIndex: 10 + p.z,
            }}
          >
            {/* 纸质拍立得本体 */}
            <div
              style={{
                width: '100%',
                height: '100%',
                background: '#fbf5e6',
                padding: '12px 12px 38px 12px',
                boxShadow:
                  '0 16px 26px rgba(0,0,0,0.45), 0 3px 6px rgba(0,0,0,0.3)',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: PHOTO_H - 12 - 38,
                  overflow: 'hidden',
                  background: '#1a140c',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                }}
              >
                <Img
                  src={image}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter:
                      'saturate(0.88) contrast(1.05) sepia(0.12) brightness(0.96)',
                  }}
                />
                {/* 扫描线噪点 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'repeating-linear-gradient(0deg, rgba(0,0,0,0.06) 0 1px, transparent 1px 3px)',
                    mixBlendMode: 'multiply',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              {/* 手写注解 */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 8,
                  textAlign: 'center',
                  fontFamily: '"Caveat", "Brush Script MT", cursive',
                  fontSize: 20,
                  color: '#2a1a0c',
                  letterSpacing: 0.5,
                }}
              >
                {caption}
              </div>
              {/* 左下编号 */}
              <div
                style={{
                  position: 'absolute',
                  left: 10,
                  bottom: 6,
                  fontFamily: '"Courier New", monospace',
                  fontSize: 8,
                  letterSpacing: 2,
                  color: 'rgba(60,35,15,0.65)',
                }}
              >
                EX-{String(i + 1).padStart(3, '0')}
              </div>
              {/* 胶带装饰（偶数照片） */}
              {i % 2 === 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: -8,
                    left: 18,
                    width: 64,
                    height: 20,
                    background:
                      'linear-gradient(180deg, rgba(255,240,180,0.78) 0%, rgba(240,215,140,0.62) 100%)',
                    transform: 'rotate(-8deg)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    borderLeft: '1px dashed rgba(120,90,30,0.25)',
                    borderRight: '1px dashed rgba(120,90,30,0.25)',
                  }}
                />
              )}
            </div>

            {/* 图钉 (pin) - 绝对定位在照片中上部 */}
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: -3,
                transform: `translateX(-50%) rotate(${-p.rot}deg) scale(${pinScale})`,
                transformOrigin: '50% 50%',
                width: 18,
                height: 18,
                borderRadius: '50%',
                background:
                  'radial-gradient(circle at 35% 30%, #ff8f8f 0%, #d62323 55%, #8c0d0d 100%)',
                boxShadow:
                  '0 3px 5px rgba(0,0,0,0.55), inset -2px -2px 3px rgba(0,0,0,0.45), inset 2px 2px 3px rgba(255,255,255,0.35)',
                zIndex: 3,
              }}
            >
              {/* 钉头高光 */}
              <div
                style={{
                  position: 'absolute',
                  left: 3,
                  top: 2,
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)',
                }}
              />
            </div>
          </div>
        );
      })}

      {/* 便利贴 */}
      {notes.map((n, i) => {
        const enter = spring({
          frame: frame - n.delay,
          fps,
          config: { damping: 13, stiffness: 140, mass: 0.7 },
        });
        const opacity = interpolate(frame - n.delay, [0, 12], [0, 1], {
          extrapolateRight: 'clamp',
        });
        const s = interpolate(enter, [0, 1], [0.5, 1]);
        const ty = interpolate(enter, [0, 1], [-18, 0]);
        return (
          <div
            key={`note-${i}`}
            style={{
              position: 'absolute',
              left: n.cx - 85,
              top: n.cy - 55,
              width: 170,
              minHeight: 110,
              padding: '14px 16px 18px',
              background: n.color,
              fontFamily: '"Caveat", "Brush Script MT", cursive',
              fontSize: n.size + 4,
              color: '#3a2a14',
              lineHeight: 1.25,
              transform: `translateY(${ty}px) rotate(${n.rot}deg) scale(${s})`,
              transformOrigin: '50% 0%',
              boxShadow:
                '0 10px 16px rgba(0,0,0,0.32), 0 2px 4px rgba(0,0,0,0.2), inset 0 -8px 14px rgba(0,0,0,0.08)',
              whiteSpace: 'pre-line',
              zIndex: 40,
              opacity,
            }}
          >
            {n.text}
            {/* 便利贴左上角卷折 */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 14,
                height: 14,
                background:
                  'linear-gradient(135deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 60%)',
              }}
            />
          </div>
        );
      })}

      {/* 底部档案分类条 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 14,
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 5,
          color: 'rgba(255,240,210,0.55)',
          textTransform: 'uppercase',
          opacity: headerOpacity,
          textShadow: '0 1px 2px rgba(0,0,0,0.7)',
          zIndex: 30,
        }}
      >
        <span>EVIDENCE · BOARD A-07</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>RED THREAD · 5 LINKS</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>DO NOT REMOVE</span>
      </div>
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #127 DJ 黑胶唱片箱（ImageVinylRecordCrate）
// 设计思路：
// 1. 模拟 DJ 翻找唱片箱的场景：若干黑胶唱片沿弧线扇形排布，前排最大、后排向两侧缩小
// 2. 每张唱片中心的圆形标签即为一张图片（circular clip），周围是刻槽纹理的环形沟
// 3. 右侧独立一张"正在播放"的唱片放在唱机上旋转，配有唱臂和指针
// 4. 动画：唱片依次从右侧滑入卡位 → 连续轻微摆动 → 唱机持续旋转（360°/8s）
// 5. 整体包裹在仿木纹 DJ 箱中，带金属铭牌 / 编号签
const VINYL_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/vinyl-1/600/600',
  'https://picsum.photos/seed/vinyl-2/600/600',
  'https://picsum.photos/seed/vinyl-3/600/600',
  'https://picsum.photos/seed/vinyl-4/600/600',
  'https://picsum.photos/seed/vinyl-5/600/600',
  'https://picsum.photos/seed/vinyl-6/600/600',
];

const VINYL_DEFAULT_TRACKS = [
  'Midnight Bloom',
  'Golden Hour',
  'Velvet Drift',
  'Neon Rain',
  'Paper Moon',
  'Low Tide',
];

const VINYL_DEFAULT_ARTISTS = [
  'Kaya Lin',
  'Otto & The Nights',
  'Serafina',
  'The Radio Ghosts',
  'June Apollo',
  'Milo Noir',
];

export const ImageVinylRecordCrate: React.FC<{
  images?: string[];
  tracks?: string[];
  artists?: string[];
  crateTitle?: string;
  subtitle?: string;
}> = ({
  images = VINYL_DEFAULT_IMAGES,
  tracks = VINYL_DEFAULT_TRACKS,
  artists = VINYL_DEFAULT_ARTISTS,
  crateTitle = 'VINYL ARCHIVE',
  subtitle = 'Side A · Six Sides of Midnight',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 左侧唱片箱中心
  const crateCx = 440;
  const crateCy = height / 2 + 32;
  const VINYL_SIZE = 260;
  const LABEL_RATIO = 0.36;

  // 扇形排布（左下角向右上扇开）
  const totalRecords = Math.min(6, images.length);
  const records = Array.from({ length: totalRecords }).map((_, i) => {
    // 前排居中最大，两侧更小往后退
    const t = i - (totalRecords - 1) / 2;
    const offsetX = t * 42;
    const offsetY = Math.abs(t) * 14;
    const scale = 1 - Math.abs(t) * 0.07;
    const rot = t * 4;
    return {
      index: i,
      image: images[i % images.length],
      track: tracks[i % tracks.length],
      artist: artists[i % artists.length],
      x: crateCx + offsetX,
      y: crateCy + offsetY,
      scale,
      rot,
      z: 10 + (totalRecords - Math.abs(Math.round(t * 2))),
      delay: 10 + i * 5,
    };
  });

  // 右侧正在播放的唱机
  const turntableCx = width - 320;
  const turntableCy = height / 2 + 20;
  const TURNTABLE_VINYL = 300;
  const spinDeg = (frame / fps) * (360 / 8); // 8 秒一圈
  const tonearmAngle = interpolate(
    spring({
      frame: frame - 30,
      fps,
      config: { damping: 18, stiffness: 60, mass: 1.2 },
    }),
    [0, 1],
    [-28, -14]
  );

  // 头部入场
  const headerOpacity = interpolate(frame, [0, 22], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(180deg, #2a1d13 0%, #1a110a 55%, #0b0705 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 木纹纹理 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(93deg, rgba(160,110,60,0.1) 0 1px, transparent 1px 7px),repeating-linear-gradient(3deg, rgba(60,30,10,0.2) 0 1px, transparent 1px 22px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      {/* 顶部聚光 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: -200,
          width: 900,
          height: 600,
          transform: 'translateX(-50%)',
          background:
            'radial-gradient(ellipse at center, rgba(255,210,150,0.25) 0%, rgba(255,210,150,0) 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerOpacity,
          zIndex: 40,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 30,
            letterSpacing: 14,
            color: '#f4d89a',
            textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            fontWeight: 700,
          }}
        >
          {crateTitle}
        </div>
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontStyle: 'italic',
            fontSize: 13,
            color: '#c9a770',
            letterSpacing: 5,
            marginTop: 4,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* === 左侧：唱片箱前沿 === */}
      <div
        style={{
          position: 'absolute',
          left: 60,
          top: crateCy - 220,
          width: 760,
          height: 450,
          background:
            'linear-gradient(180deg, #5a3b22 0%, #3e2814 60%, #2a1a0b 100%)',
          border: '3px solid rgba(20,10,4,0.8)',
          borderRadius: 6,
          boxShadow:
            '0 22px 40px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,200,140,0.08), inset 0 -14px 28px rgba(0,0,0,0.4)',
          zIndex: 2,
          overflow: 'hidden',
        }}
      >
        {/* 木箱内侧阴影 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 50% 95%, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 60%)',
            pointerEvents: 'none',
          }}
        />
        {/* 金属铭牌 */}
        <div
          style={{
            position: 'absolute',
            left: 18,
            top: 16,
            padding: '6px 14px',
            background:
              'linear-gradient(180deg, #e8d9a9 0%, #b89a60 55%, #856635 100%)',
            color: '#2a1a08',
            fontFamily: '"Courier New", monospace',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 4,
            boxShadow:
              '0 2px 4px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
            borderRadius: 2,
            opacity: headerOpacity,
          }}
        >
          CRATE A · SIDE 01
        </div>
        {/* 右上"IN ROTATION" */}
        <div
          style={{
            position: 'absolute',
            right: 18,
            top: 18,
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 3,
            color: 'rgba(255,220,170,0.7)',
            opacity: headerOpacity,
          }}
        >
          <span style={{color:'#ff6b6b', marginRight:6}}>●</span>
          IN ROTATION · {String(totalRecords).padStart(2, '0')}
        </div>
      </div>

      {/* 唱片本体（扇形排布） */}
      {records.map((r) => {
        const enter = spring({
          frame: frame - r.delay,
          fps,
          config: { damping: 15, stiffness: 110, mass: 0.8 },
        });
        const slide = interpolate(enter, [0, 1], [260, 0]);
        const opacity = interpolate(frame - r.delay, [0, 14], [0, 1], {
          extrapolateRight: 'clamp',
        });

        // 入场后轻微摆动
        const idle = Math.max(0, frame - r.delay - 26);
        const wobble = Math.sin((idle / fps) * 0.9 + r.index) * 0.8;
        // 唱片内自身也缓慢自转（整体一致）
        const selfSpin = (idle / fps) * 12;

        return (
          <div
            key={r.index}
            style={{
              position: 'absolute',
              left: r.x - VINYL_SIZE / 2,
              top: r.y - VINYL_SIZE / 2,
              width: VINYL_SIZE,
              height: VINYL_SIZE,
              transform: `translateX(${slide}px) scale(${r.scale}) rotate(${
                r.rot + wobble
              }deg)`,
              transformOrigin: '50% 50%',
              opacity,
              zIndex: 20 + r.z,
              filter: 'drop-shadow(0 14px 18px rgba(0,0,0,0.55))',
            }}
          >
            {/* 唱片外圆（黑胶） */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: '#0a0a0a',
                overflow: 'hidden',
                transform: `rotate(${selfSpin}deg)`,
              }}
            >
              {/* 径向沟槽：用 repeating-radial-gradient */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background:
                    'repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px),radial-gradient(circle at 35% 35%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 60%)',
                }}
              />
              {/* 中央圆形标签 = 图片 */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: VINYL_SIZE * LABEL_RATIO * 2,
                  height: VINYL_SIZE * LABEL_RATIO * 2,
                  marginLeft: -VINYL_SIZE * LABEL_RATIO,
                  marginTop: -VINYL_SIZE * LABEL_RATIO,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  boxShadow:
                    'inset 0 0 0 2px rgba(244,216,154,0.6), 0 0 0 2px rgba(0,0,0,0.35)',
                }}
              >
                <Img
                  src={r.image}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(1.05) contrast(1.08)',
                  }}
                />
                {/* 标签四周光圈 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.3) 100%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              {/* 中心针孔 */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 10,
                  height: 10,
                  marginLeft: -5,
                  marginTop: -5,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, #f4d89a 0%, #3a2810 65%, #000 100%)',
                  boxShadow: '0 0 2px rgba(0,0,0,0.9)',
                }}
              />
            </div>
          </div>
        );
      })}

      {/* === 右侧：正在播放的唱机（不跟随箱子内扇形旋转） === */}
      {/* 唱机底座 */}
      <div
        style={{
          position: 'absolute',
          left: turntableCx - 210,
          top: turntableCy - 210,
          width: 420,
          height: 420,
          background:
            'linear-gradient(145deg, #262626 0%, #181818 60%, #0b0b0b 100%)',
          borderRadius: 10,
          border: '2px solid rgba(255,255,255,0.06)',
          boxShadow:
            '0 20px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
          zIndex: 25,
          opacity: headerOpacity,
        }}
      >
        {/* 品牌字样 */}
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 18,
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            letterSpacing: 5,
            color: '#f4d89a',
          }}
        >
          TONE · 33⅓ RPM
        </div>
        {/* 节奏指示灯 */}
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            letterSpacing: 3,
            color: 'rgba(255,220,170,0.75)',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#6bff95',
              boxShadow: '0 0 6px rgba(107,255,149,0.85)',
            }}
          />
          PLAY
        </div>
      </div>

      {/* 正在播放的唱片（旋转） */}
      <div
        style={{
          position: 'absolute',
          left: turntableCx - TURNTABLE_VINYL / 2,
          top: turntableCy - TURNTABLE_VINYL / 2,
          width: TURNTABLE_VINYL,
          height: TURNTABLE_VINYL,
          borderRadius: '50%',
          background: '#0a0a0a',
          overflow: 'hidden',
          transform: `rotate(${spinDeg}deg)`,
          boxShadow:
            '0 10px 18px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.04)',
          zIndex: 30,
          opacity: headerOpacity,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background:
              'repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)',
          }}
        />
        {/* 中央标签 */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: TURNTABLE_VINYL * LABEL_RATIO * 2,
            height: TURNTABLE_VINYL * LABEL_RATIO * 2,
            marginLeft: -TURNTABLE_VINYL * LABEL_RATIO,
            marginTop: -TURNTABLE_VINYL * LABEL_RATIO,
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: 'inset 0 0 0 2px rgba(244,216,154,0.7)',
          }}
        >
          <Img
            src={images[0]}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'saturate(1.08) contrast(1.1)',
            }}
          />
        </div>
        {/* 中心针孔 */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 12,
            height: 12,
            marginLeft: -6,
            marginTop: -6,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, #f4d89a 0%, #3a2810 60%, #000 100%)',
          }}
        />
      </div>

      {/* 唱臂 */}
      <div
        style={{
          position: 'absolute',
          left: turntableCx + TURNTABLE_VINYL / 2 - 40,
          top: turntableCy - TURNTABLE_VINYL / 2 - 40,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 35% 30%, #e6e6e6 0%, #8a8a8a 60%, #444 100%)',
          boxShadow:
            '0 2px 4px rgba(0,0,0,0.6), inset 1px 1px 2px rgba(255,255,255,0.4)',
          transformOrigin: '50% 50%',
          zIndex: 40,
          opacity: headerOpacity,
        }}
      >
        {/* 唱臂杆 */}
        <div
          style={{
            position: 'absolute',
            left: 7,
            top: 7,
            width: 220,
            height: 5,
            background:
              'linear-gradient(180deg, #d4d4d4 0%, #7b7b7b 55%, #3a3a3a 100%)',
            borderRadius: 3,
            transformOrigin: '0 50%',
            transform: `rotate(${tonearmAngle + 130}deg)`,
            boxShadow: '0 2px 3px rgba(0,0,0,0.55)',
          }}
        >
          {/* 唱头 */}
          <div
            style={{
              position: 'absolute',
              right: -10,
              top: -6,
              width: 22,
              height: 14,
              background:
                'linear-gradient(180deg, #3a3a3a 0%, #1a1a1a 100%)',
              borderRadius: 2,
            }}
          />
        </div>
      </div>

      {/* 当前曲目信息（唱机下方） */}
      <div
        style={{
          position: 'absolute',
          left: turntableCx - 180,
          top: turntableCy + 170,
          width: 360,
          textAlign: 'center',
          opacity: interpolate(frame - 40, [0, 18], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
          zIndex: 40,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            letterSpacing: 5,
            color: 'rgba(255,220,170,0.7)',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          Now Playing · Track 01
        </div>
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontSize: 22,
            fontStyle: 'italic',
            color: '#f4d89a',
            letterSpacing: 1,
          }}
        >
          {tracks[0]}
        </div>
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontSize: 12,
            color: '#b8935c',
            letterSpacing: 3,
            marginTop: 2,
            textTransform: 'uppercase',
          }}
        >
          {artists[0]}
        </div>
      </div>

      {/* 底部页脚 */}
      <div
        style={{
          position: 'absolute',
          bottom: 14,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: 'rgba(255,220,170,0.45)',
          textTransform: 'uppercase',
          opacity: headerOpacity,
        }}
      >
        <span>33⅓ RPM</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>SIX RECORDS IN CRATE</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>MMXXVI EDITION</span>
      </div>

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// #128 集邮册 · 邮票收藏页（ImageStampAlbum）
// 设计思路：
// 1. 复刻老式集邮册对页：年代感米黄色纸张、竖纹、边缘氧化
// 2. 6 张带齿孔边的"邮票"，每张邮票 = 国名 + 面值 + 图片 + 年份
// 3. 邮票按网格整齐排布（3 列 2 行），带分隔暗线，像保护槽
// 4. 每张邮票上叠加一枚"邮戳圆环"斜向盖下来（bounce spring），半透明
// 5. 邮票下方有目录编号 (Sc. # / Mi. #)，模拟 Scott / Michel 邮票目录
// 6. 动画：标题入场 → 邮票按对角波依次下落卡位 → 邮戳延迟盖下 → 说明文字淡入
const STAMP_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/stamp-ala/500/400',
  'https://picsum.photos/seed/stamp-bor/500/400',
  'https://picsum.photos/seed/stamp-cat/500/400',
  'https://picsum.photos/seed/stamp-del/500/400',
  'https://picsum.photos/seed/stamp-eph/500/400',
  'https://picsum.photos/seed/stamp-fjo/500/400',
];

const STAMP_DEFAULT_COUNTRIES = [
  'HELVETIA',
  'NIPPON',
  'MAGYAR POSTA',
  'POLSKA',
  'DANMARK',
  'ÉIRE',
];

const STAMP_DEFAULT_VALUES = ['25 c', '¥ 80', '40 f', '1.50 zł', '3 kr', '€ 0.60'];
const STAMP_DEFAULT_YEARS = ['1962', '1955', '1968', '1971', '1959', '1966'];

const STAMP_DEFAULT_CAPTIONS = [
  'Alpine Morning',
  'Kōyō',
  'Danube Light',
  'Tatra Song',
  'The Little Mermaid',
  'Cliffs of Moher',
];

export const ImageStampAlbum: React.FC<{
  images?: string[];
  countries?: string[];
  values?: string[];
  years?: string[];
  captions?: string[];
  albumTitle?: string;
  pageLabel?: string;
}> = ({
  images = STAMP_DEFAULT_IMAGES,
  countries = STAMP_DEFAULT_COUNTRIES,
  values = STAMP_DEFAULT_VALUES,
  years = STAMP_DEFAULT_YEARS,
  captions = STAMP_DEFAULT_CAPTIONS,
  albumTitle = 'PHILATELIA · ATLAS OF LIGHT',
  pageLabel = 'Page XXIV · Europe & Asia · 1950-1970',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 页面安全边距
  const MARGIN_X = 64;
  const MARGIN_TOP = 108;
  const MARGIN_BOTTOM = 64;
  const pageW = width - MARGIN_X * 2;
  const pageH = height - MARGIN_TOP - MARGIN_BOTTOM;

  // 网格：3 列 x 2 行
  const COLS = 3;
  const ROWS = 2;
  const CELL_GAP_X = 36;
  const CELL_GAP_Y = 28;
  const cellW = (pageW - CELL_GAP_X * (COLS - 1)) / COLS;
  const cellH = (pageH - CELL_GAP_Y * (ROWS - 1)) / ROWS;

  // 邮票尺寸（在 cell 内留白，呈现保护槽观感）
  const stampW = cellW * 0.82;
  const stampH = Math.min(cellH * 0.86, stampW * 0.82);

  // 齿孔
  const TEETH_SIZE = 8;
  const TEETH_COUNT_X = Math.max(10, Math.round(stampW / 18));
  const TEETH_COUNT_Y = Math.max(8, Math.round(stampH / 18));

  // 入场
  const headerEnter = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 130, mass: 0.9 },
  });
  const headerY = interpolate(headerEnter, [0, 1], [-20, 0]);
  const headerOp = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 40%, #f6eccf 0%, #e8d7a8 65%, #c9b27a 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 纸张纹理：竖纹 + 微噪点 */}
      <AbsoluteFill
        style={{
          background:
            'repeating-linear-gradient(90deg, rgba(130,90,40,0.06) 0 1px, transparent 1px 4px),repeating-linear-gradient(0deg, rgba(120,80,30,0.04) 0 1px, transparent 1px 3px)',
          mixBlendMode: 'multiply',
          pointerEvents: 'none',
        }}
      />
      {/* 页面边缘氧化（四周褐变暗角） */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(110,60,20,0.25) 100%)',
          pointerEvents: 'none',
        }}
      />
      {/* 装订孔（左侧三孔） */}
      {[0, 1, 2].map((i) => (
        <div
          key={`hole-${i}`}
          style={{
            position: 'absolute',
            left: 24,
            top: 120 + i * ((height - 240) / 2),
            width: 18,
            height: 18,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(60,40,20,0.7) 0%, rgba(60,40,20,0) 70%), #d4c08a',
            boxShadow:
              'inset 0 2px 3px rgba(0,0,0,0.55), 0 1px 1px rgba(255,240,210,0.6)',
            zIndex: 5,
          }}
        />
      ))}

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerOp,
          transform: `translateY(${headerY}px)`,
          zIndex: 30,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 28,
            letterSpacing: 12,
            color: '#4a2d14',
            fontWeight: 700,
            textShadow: '0 1px 0 rgba(255,240,210,0.5)',
          }}
        >
          {albumTitle}
        </div>
        <div
          style={{
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
          }}
        >
          <div
            style={{
              height: 1,
              width: 70,
              background:
                'linear-gradient(90deg, transparent, rgba(74,45,20,0.55), transparent)',
            }}
          />
          <div
            style={{
              fontFamily: '"Georgia", serif',
              fontStyle: 'italic',
              fontSize: 13,
              letterSpacing: 4,
              color: '#6b4824',
            }}
          >
            {pageLabel}
          </div>
          <div
            style={{
              height: 1,
              width: 70,
              background:
                'linear-gradient(90deg, transparent, rgba(74,45,20,0.55), transparent)',
            }}
          />
        </div>
      </div>

      {/* 邮票网格 */}
      {Array.from({ length: COLS * ROWS }).map((_, i) => {
        if (i >= images.length) return null;
        const col = i % COLS;
        const row = Math.floor(i / COLS);
        const cellX = MARGIN_X + col * (cellW + CELL_GAP_X);
        const cellY = MARGIN_TOP + row * (cellH + CELL_GAP_Y);
        // 邮票居中于 cell
        const stampX = cellX + (cellW - stampW) / 2;
        const stampY = cellY + (cellH - stampH) / 2 - 6;

        // 对角波入场
        const delay = 14 + (col + row) * 10 + i * 2;
        const enter = spring({
          frame: frame - delay,
          fps,
          config: { damping: 14, stiffness: 100, mass: 0.8 },
        });
        const dropY = interpolate(enter, [0, 1], [-28, 0]);
        const startRot = interpolate(enter, [0, 1], [(i % 2 === 0 ? -4 : 4), 0]);
        const s = interpolate(enter, [0, 1], [0.88, 1]);
        const op = interpolate(frame - delay, [0, 14], [0, 1], {
          extrapolateRight: 'clamp',
        });

        // 邮戳延迟盖章
        const stampDelay = delay + 14;
        const stampEnter = spring({
          frame: frame - stampDelay,
          fps,
          config: { damping: 10, stiffness: 180, mass: 0.6 },
        });
        const stampScale = interpolate(stampEnter, [0, 1], [2.4, 1]);
        const stampOp = interpolate(
          frame - stampDelay,
          [0, 8, 18],
          [0, 1, 0.88],
          { extrapolateRight: 'clamp' }
        );
        // 邮戳位置 / 倾角（每张邮票略有差异）
        const postmarkRot = (i % 3) * 6 - 8;
        const postmarkCx = stampX + stampW * (0.65 + (i % 2) * 0.08);
        const postmarkCy = stampY + stampH * (0.2 + (i % 2) * 0.05);

        // 保护槽（邮票下的凹槽）
        return (
          <React.Fragment key={i}>
            {/* 保护槽 */}
            <div
              style={{
                position: 'absolute',
                left: stampX - 8,
                top: stampY - 6,
                width: stampW + 16,
                height: stampH + 12 + 34, // 下方标签区
                background:
                  'linear-gradient(180deg, rgba(110,70,30,0.16) 0%, rgba(110,70,30,0.05) 8%, rgba(110,70,30,0.05) 90%, rgba(110,70,30,0.16) 100%)',
                border: '1px solid rgba(110,70,30,0.3)',
                borderRadius: 2,
                boxShadow: 'inset 0 0 0 1px rgba(255,240,210,0.2)',
                opacity: headerOp,
                zIndex: 4,
              }}
            />
            {/* 邮票本体 */}
            <div
              style={{
                position: 'absolute',
                left: stampX,
                top: stampY,
                width: stampW,
                height: stampH,
                background: '#faf3df',
                transform: `translateY(${dropY}px) rotate(${startRot}deg) scale(${s})`,
                opacity: op,
                boxShadow:
                  '0 6px 10px rgba(80,50,20,0.35), 0 1px 2px rgba(80,50,20,0.25)',
                zIndex: 10,
              }}
            >
              {/* 齿孔（四周） */}
              {/* 顶部 */}
              {Array.from({ length: TEETH_COUNT_X }).map((_, tx) => (
                <div
                  key={`t-top-${tx}`}
                  style={{
                    position: 'absolute',
                    left: (stampW / TEETH_COUNT_X) * tx + stampW / TEETH_COUNT_X / 2 - TEETH_SIZE / 2,
                    top: -TEETH_SIZE / 2,
                    width: TEETH_SIZE,
                    height: TEETH_SIZE,
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, #f6eccf 0%, #e8d7a8 80%)',
                  }}
                />
              ))}
              {/* 底部 */}
              {Array.from({ length: TEETH_COUNT_X }).map((_, tx) => (
                <div
                  key={`t-bot-${tx}`}
                  style={{
                    position: 'absolute',
                    left: (stampW / TEETH_COUNT_X) * tx + stampW / TEETH_COUNT_X / 2 - TEETH_SIZE / 2,
                    bottom: -TEETH_SIZE / 2,
                    width: TEETH_SIZE,
                    height: TEETH_SIZE,
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, #f6eccf 0%, #e8d7a8 80%)',
                  }}
                />
              ))}
              {/* 左侧 */}
              {Array.from({ length: TEETH_COUNT_Y }).map((_, ty) => (
                <div
                  key={`t-l-${ty}`}
                  style={{
                    position: 'absolute',
                    top: (stampH / TEETH_COUNT_Y) * ty + stampH / TEETH_COUNT_Y / 2 - TEETH_SIZE / 2,
                    left: -TEETH_SIZE / 2,
                    width: TEETH_SIZE,
                    height: TEETH_SIZE,
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, #f6eccf 0%, #e8d7a8 80%)',
                  }}
                />
              ))}
              {/* 右侧 */}
              {Array.from({ length: TEETH_COUNT_Y }).map((_, ty) => (
                <div
                  key={`t-r-${ty}`}
                  style={{
                    position: 'absolute',
                    top: (stampH / TEETH_COUNT_Y) * ty + stampH / TEETH_COUNT_Y / 2 - TEETH_SIZE / 2,
                    right: -TEETH_SIZE / 2,
                    width: TEETH_SIZE,
                    height: TEETH_SIZE,
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, #f6eccf 0%, #e8d7a8 80%)',
                  }}
                />
              ))}

              {/* 邮票内边框 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 8,
                  border: '1px solid rgba(80,50,20,0.55)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,240,210,0.4)',
                  overflow: 'hidden',
                }}
              >
                {/* 图片 */}
                <Img
                  src={images[i]}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter:
                      'saturate(0.85) contrast(1.05) sepia(0.18) brightness(0.96)',
                  }}
                />
                {/* 邮票上覆盖半透明暖色调，模拟旧印刷 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(250,243,223,0.12) 0%, rgba(250,243,223,0) 40%, rgba(110,70,30,0.18) 100%)',
                    pointerEvents: 'none',
                  }}
                />
                {/* 顶部国名 */}
                <div
                  style={{
                    position: 'absolute',
                    top: 4,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontFamily: '"Georgia", serif',
                    fontSize: 9,
                    letterSpacing: 3,
                    color: '#fbeccf',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  {countries[i % countries.length]}
                </div>
                {/* 右下面值 */}
                <div
                  style={{
                    position: 'absolute',
                    right: 6,
                    bottom: 4,
                    fontFamily: '"Georgia", serif',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fbeccf',
                    letterSpacing: 1,
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                  }}
                >
                  {values[i % values.length]}
                </div>
                {/* 左下年份 */}
                <div
                  style={{
                    position: 'absolute',
                    left: 6,
                    bottom: 4,
                    fontFamily: '"Courier New", monospace',
                    fontSize: 9,
                    color: 'rgba(251,236,207,0.85)',
                    letterSpacing: 2,
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                  }}
                >
                  {years[i % years.length]}
                </div>
              </div>
            </div>

            {/* 邮戳（盖在邮票上） */}
            <div
              style={{
                position: 'absolute',
                left: postmarkCx - 55,
                top: postmarkCy - 55,
                width: 110,
                height: 110,
                opacity: stampOp * 0.85,
                transform: `rotate(${postmarkRot}deg) scale(${stampScale})`,
                transformOrigin: '50% 50%',
                mixBlendMode: 'multiply',
                zIndex: 20,
                pointerEvents: 'none',
              }}
            >
              {/* 外环 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '2.5px solid rgba(80,20,10,0.8)',
                  boxShadow: 'inset 0 0 0 1.5px rgba(80,20,10,0.45)',
                }}
              />
              {/* 内环 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 16,
                  borderRadius: '50%',
                  border: '1.5px solid rgba(80,20,10,0.7)',
                }}
              />
              {/* 顶部弧形城市名：用独立 div 直接放文字 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  paddingTop: 6,
                }}
              >
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 10,
                    letterSpacing: 3,
                    color: 'rgba(80,20,10,0.85)',
                    fontWeight: 700,
                  }}
                >
                  ★ POSTA ★
                </div>
              </div>
              {/* 中部日期 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    fontFamily: '"Courier New", monospace',
                    fontSize: 9,
                    color: 'rgba(80,20,10,0.8)',
                    letterSpacing: 1,
                  }}
                >
                  {(18 + (i * 3)) % 28 + 1}
                  {' · '}
                  {['I', 'II', 'III', 'IV', 'V', 'VI'][i % 6]}
                </div>
                <div
                  style={{
                    fontFamily: '"Courier New", monospace',
                    fontSize: 11,
                    color: 'rgba(80,20,10,0.85)',
                    letterSpacing: 2,
                    fontWeight: 700,
                  }}
                >
                  {years[i % years.length]}
                </div>
              </div>
              {/* 底部弧形 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  paddingBottom: 6,
                }}
              >
                <div
                  style={{
                    fontFamily: '"Georgia", serif',
                    fontSize: 9,
                    letterSpacing: 2,
                    color: 'rgba(80,20,10,0.8)',
                  }}
                >
                  PAR AVION
                </div>
              </div>
            </div>

            {/* 邮票下方：目录编号 + 标题 */}
            <div
              style={{
                position: 'absolute',
                left: stampX - 8,
                top: stampY + stampH + 10,
                width: stampW + 16,
                textAlign: 'center',
                opacity: interpolate(
                  frame - delay - 8,
                  [0, 14],
                  [0, 1],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
                ),
                zIndex: 8,
              }}
            >
              <div
                style={{
                  fontFamily: '"Georgia", serif',
                  fontStyle: 'italic',
                  fontSize: 12,
                  color: '#3d2614',
                  letterSpacing: 1,
                }}
              >
                {captions[i % captions.length]}
              </div>
              <div
                style={{
                  fontFamily: '"Courier New", monospace',
                  fontSize: 9,
                  color: '#7a5424',
                  letterSpacing: 3,
                  marginTop: 2,
                  textTransform: 'uppercase',
                }}
              >
                Sc. #{1820 + i * 17} · Mi. #{2104 + i * 23}
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* 底部目录说明 */}
      <div
        style={{
          position: 'absolute',
          bottom: 22,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          fontFamily: '"Georgia", serif',
          fontStyle: 'italic',
          fontSize: 11,
          letterSpacing: 4,
          color: '#6b4824',
          textTransform: 'uppercase',
          opacity: headerOp,
        }}
      >
        <span>Catalogued MMXXVI</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>Six Impressions · One Page</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>The Atlas Continues</span>
      </div>
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ============================================================
// #129 3D 封面流 · ImageCoverflow3D
// ------------------------------------------------------------
// 设计思路：
// 1. 深色舞台 + 地面网格反光 + 头顶聚光灯光束，影院首映氛围
// 2. 中央封面正面大图（亮）+ 左右各两张封面以 3D 透视斜切叠加
// 3. 每张封面下方有实时倒影（镜像 + 渐隐）
// 4. "currentIndex" 持续推进，画面做平滑切换：
//    近处的封面一路挤向中心成为主角，再向外退场
// 5. 顶部发光的专辑标题与副标题，随主角切换而渐隐淡入
// ============================================================
const COVERFLOW_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/cf-aurora/700/700',
  'https://picsum.photos/seed/cf-basalt/700/700',
  'https://picsum.photos/seed/cf-cascade/700/700',
  'https://picsum.photos/seed/cf-duskmoth/700/700',
  'https://picsum.photos/seed/cf-emberly/700/700',
  'https://picsum.photos/seed/cf-folio/700/700',
  'https://picsum.photos/seed/cf-glacier/700/700',
  'https://picsum.photos/seed/cf-halogen/700/700',
];

const COVERFLOW_DEFAULT_TITLES = [
  'AURORA',
  'BASALT',
  'CASCADE',
  'DUSK MOTH',
  'EMBERLY',
  'FOLIO',
  'GLACIER',
  'HALOGEN',
];

const COVERFLOW_DEFAULT_ARTISTS = [
  'Nocturne Ensemble',
  'Iron & Tide',
  'Foxglove',
  'The Lantern Choir',
  'Ash & Chant',
  'Parallel Rooms',
  'North Sea Echo',
  'Vela Project',
];

export const ImageCoverflow3D: React.FC<{
  images?: string[];
  titles?: string[];
  artists?: string[];
  showcaseTitle?: string;
  showcaseSubtitle?: string;
  secondsPerCover?: number;
}> = ({
  images = COVERFLOW_DEFAULT_IMAGES,
  titles = COVERFLOW_DEFAULT_TITLES,
  artists = COVERFLOW_DEFAULT_ARTISTS,
  showcaseTitle = 'SELECTED WORKS',
  showcaseSubtitle = 'Side A · A Coverflow Of Light',
  secondsPerCover = 1.8,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const framesPerCover = fps * secondsPerCover;

  // 连续封面索引 + 分段缓动：每一段使用 cosine ease-in-out，
  // 让中心主角有"停留感"，过渡更丝滑
  const rawIndex = frame / framesPerCover;
  const step = Math.floor(rawIndex);
  const t = rawIndex - step;
  // ease-in-out-cubic：启动平稳、中段加速、尾段缓停，整体更柔和
  const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const flowIndex = step + eased;
  const activeIndex = Math.round(flowIndex) % images.length;

  // 舞台参数
  const coverW = Math.min(360, width * 0.28);
  const coverH = coverW;
  const centerY = height * 0.5 - 20;
  const perspective = 1800;

  // 平滑步进函数：让近处 rel<1 的移动更有"推入"手感
  const smoothstep = (x: number) => {
    const v = Math.min(1, Math.max(0, x));
    return v * v * (3 - 2 * v);
  };

  // 每张封面渲染参数
  const getCoverStyle = (i: number) => {
    // 相对主角的位置（连续值）
    let rel = i - flowIndex;
    // 对称环绕：保证超过一半的卡牌从另一侧出场
    const half = images.length / 2;
    if (rel > half) rel -= images.length;
    if (rel < -half) rel += images.length;

    const absRel = Math.abs(rel);
    const visible = absRel < 3.8;
    // 连续的方向系数（-1/0/+1），rel=0 时为 0，但绝不会让卡片重叠在中心
    const side = absRel < 0.0001 ? 0 : rel / absRel;

    // 近段 0..1 用 smoothstep：主角一出来就快速被推到侧边，不会与中心重叠
    const near = smoothstep(absRel);
    const far = Math.max(0, absRel - 1);

    // 水平位移：确保 rel=1 时卡片已经完全让出中心区域
    const x = side * (near * coverW * 0.62 + far * coverW * 0.48);

    // 深度：近段渐推、远段继续后退，确保 z-fighting 避免
    const z = -near * 200 - far * 140;

    // 旋转：用 smoothstep 快速拉到 ~62°，远处再微增让层次清晰
    const rotY = side * (near * 62 + far * 6);

    // 缩放与亮度：用缓动曲线，避免中心被"灰色幽灵"覆盖
    const scale = Math.max(0.58, 1 - near * 0.16 - far * 0.09);
    const brightness = Math.max(0.32, 1 - near * 0.28 - far * 0.16);
    // 远处卡片轻微模糊，营造景深、也掩盖采样锯齿
    const blurPx = far * 1.6;

    return {
      visible,
      rel,
      absRel,
      x,
      z,
      rotY,
      scale,
      brightness,
      blurPx,
      zIndex: Math.round(2000 - absRel * 120),
    };
  };

  // 主角标题淡入淡出：以 |eased - 0.5| 为中心做平滑，切换点最淡、中心最亮
  const titleProgress = Math.abs(eased - 0.5) * 2; // 0 居中，1 切换瞬间
  const titleFade = 1 - smoothstep(titleProgress);

  // 聚光灯脉冲（更柔和）
  const spot = 0.92 + Math.sin(frame / 26) * 0.06;

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 20%, #1c1a22 0%, #0a0910 55%, #060509 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 顶部聚光灯锥光（放置在舞台之后，避免光束穿过封面造成"十字痕" ） */}
      <div
        style={{
          position: 'absolute',
          top: -120,
          left: '50%',
          width: width * 0.95,
          height: height * 0.55,
          marginLeft: -(width * 0.95) / 2,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(255,220,180,0.22) 0%, rgba(255,220,180,0.08) 28%, transparent 55%)',
          opacity: spot,
          mixBlendMode: 'screen',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {/* 细光束：只占画面顶部，宽度更窄，避免横穿封面 */}
      {[-1, 1].map((s) => (
        <div
          key={`beam-${s}`}
          style={{
            position: 'absolute',
            top: -60,
            left: '50%',
            width: 2,
            height: height * 0.42,
            transform: `translateX(${s * 360}px) rotate(${s * 10}deg)`,
            transformOrigin: '50% 0%',
            background:
              'linear-gradient(180deg, rgba(255,210,160,0.45) 0%, rgba(255,210,160,0) 100%)',
            filter: 'blur(2.5px)',
            mixBlendMode: 'screen',
            opacity: 0.45 * spot,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ))}

      {/* 地面：透视网格 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: height * 0.42,
          background:
            'linear-gradient(180deg, transparent 0%, rgba(40,38,56,0.6) 25%, rgba(15,13,22,0.95) 100%), repeating-linear-gradient(90deg, rgba(140,140,180,0.1) 0 1px, transparent 1px 56px), repeating-linear-gradient(0deg, rgba(140,140,180,0.08) 0 1px, transparent 1px 40px)',
          zIndex: 1,
          transform: 'perspective(700px) rotateX(62deg)',
          transformOrigin: 'center top',
          pointerEvents: 'none',
        }}
      />
      {/* 地面辉光 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: height * 0.18,
          width: 700,
          height: 220,
          marginLeft: -350,
          background:
            'radial-gradient(ellipse at center, rgba(255,200,160,0.22) 0%, rgba(255,200,160,0) 70%)',
          mixBlendMode: 'screen',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* 顶部品牌条 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1500,
        }}
      >
        <div
          style={{
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontSize: 11,
            letterSpacing: 10,
            color: 'rgba(255,220,180,0.6)',
            textTransform: 'uppercase',
          }}
        >
          {showcaseTitle}
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Georgia", serif',
            fontStyle: 'italic',
            fontSize: 13,
            letterSpacing: 4,
            color: 'rgba(255,235,210,0.75)',
          }}
        >
          {showcaseSubtitle}
        </div>
      </div>

      {/* 3D 舞台 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width,
          height,
          perspective: `${perspective}px`,
          perspectiveOrigin: '50% 45%',
          transformStyle: 'preserve-3d',
          zIndex: 10,
        }}
      >
        {images.map((src, i) => {
          const s = getCoverStyle(i);
          if (!s.visible) return null;

          // 倒影仅在"邻近卡片"上渲染，远处卡片不画倒影，避免镜像泄漏到中心卡
          const reflectionOpacity = Math.max(0, 0.42 * (1 - s.absRel / 1.6));
          const showReflection = reflectionOpacity > 0.02;
          // 非主角暗化蒙层：靠近 0 时透明，拉远逐步加深，但有上限
          const dim = Math.min(0.5, s.absRel * 0.22);

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: width / 2 - coverW / 2,
                top: centerY - coverH / 2,
                width: coverW,
                height: coverH,
                transform: `translate3d(${s.x.toFixed(2)}px, 0, ${s.z.toFixed(2)}px) rotateY(${s.rotY.toFixed(2)}deg) scale(${s.scale.toFixed(3)})`,
                transformStyle: 'preserve-3d',
                transformOrigin: '50% 50%',
                zIndex: s.zIndex,
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              {/* 封面 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  boxShadow:
                    '0 18px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                  background: '#0a0910',
                  filter: `brightness(${s.brightness.toFixed(3)}) saturate(1.05) blur(${s.blurPx.toFixed(2)}px)`,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <Img
                  src={src}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                {/* 玻璃反光（不使用 screen 混合，避免叠加到其它封面） */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0) 55%)',
                    pointerEvents: 'none',
                  }}
                />
                {/* 非主角叠加暗色蒙层 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `rgba(8,6,12,${dim.toFixed(3)})`,
                    pointerEvents: 'none',
                  }}
                />
                {/* 封面角标 */}
                <div
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    padding: '2px 6px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,220,180,0.4)',
                    fontFamily: '"Courier New", monospace',
                    fontSize: 10,
                    color: 'rgba(255,230,200,0.9)',
                    letterSpacing: 2,
                  }}
                >
                  VOL.{String(i + 1).padStart(2, '0')}
                </div>
              </div>
              {/* 倒影：只在靠近中心的封面才绘制，且剪裁在封面正下方 */}
              {showReflection && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: coverH,
                    width: coverW,
                    height: coverH * 0.62,
                    transform: 'scaleY(-1)',
                    transformOrigin: 'top center',
                    overflow: 'hidden',
                    opacity: reflectionOpacity,
                    filter: `brightness(${(s.brightness * 0.7).toFixed(3)}) blur(0.6px)`,
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    pointerEvents: 'none',
                  }}
                >
                  <Img
                    src={src}
                    style={{
                      width: '100%',
                      height: coverH,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(0deg, rgba(10,8,14,0) 0%, rgba(10,8,14,0.65) 55%, rgba(10,8,14,0.98) 100%)',
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 主角标题 · 随主角切换而淡入淡出 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 90,
          textAlign: 'center',
          opacity: titleFade,
          zIndex: 2000,
        }}
      >
        <div
          style={{
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontSize: 44,
            fontWeight: 800,
            letterSpacing: 12,
            color: '#fff5e0',
            textShadow: '0 3px 22px rgba(255,200,130,0.55)',
          }}
        >
          {titles[activeIndex % titles.length]}
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily: '"Georgia", serif',
            fontStyle: 'italic',
            fontSize: 14,
            letterSpacing: 5,
            color: 'rgba(255,220,180,0.75)',
          }}
        >
          — {artists[activeIndex % artists.length]} —
        </div>
      </div>

      {/* 进度刻度 */}
      <div
        style={{
          position: 'absolute',
          bottom: 42,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 10,
          zIndex: 2000,
        }}
      >
        {images.map((_, i) => {
          // 与 flowIndex 对齐的"接近度"——做平滑宽度/亮度过渡
          let rel = i - flowIndex;
          const half = images.length / 2;
          if (rel > half) rel -= images.length;
          if (rel < -half) rel += images.length;
          const proximity = Math.max(0, 1 - Math.abs(rel));
          const w = 6 + proximity * 22;
          const glow = proximity;
          return (
            <div
              key={`dot-${i}`}
              style={{
                width: w,
                height: 4,
                borderRadius: 2,
                background: `rgba(255,215,158,${(0.25 + glow * 0.75).toFixed(3)})`,
                boxShadow: glow > 0.1 ? `0 0 ${(4 + glow * 10).toFixed(1)}px rgba(255,200,140,${(glow * 0.8).toFixed(3)})` : 'none',
              }}
            />
          );
        })}
      </div>

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.6) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ============================================================
// #130 星座图集 · ImageConstellationAtlas（古典天文观测所 · 巨型星盘版）
// ------------------------------------------------------------
// 设计思路（重构版，更大气更新颖）：
// 1. 深空宇宙 + 多层极光云 + 银河尘云 + 300 颗随机闪烁星
// 2. 画面中央：巨型古典星盘 —— 金色同心铜环、黄道十二宫符号环、
//    72 刻度、放射 24 等分线、八芒星核心纹章
// 3. 8 张图片作为 8 颗"图星"，以椭圆光之轨道环绕中央星盘
// 4. 每个节点：金色发光环 + 冷调图像 + 径向光芒十字 + 外侧标牌
// 5. 连线分两层：节点→中心的金色光束 + 相邻节点间的星座链
//    链条依次点亮，并有流光粒子沿链奔流
// 6. 三道流星以不同角度划过天幕；整体缓慢自转呼吸
// 7. 四角古典角花 + 顶部徽章大标题 + 底部观测仪刻度条
// ============================================================
const CONSTELL_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/star-sirius/400/400',
  'https://picsum.photos/seed/star-vega/400/400',
  'https://picsum.photos/seed/star-rigel/400/400',
  'https://picsum.photos/seed/star-altair/400/400',
  'https://picsum.photos/seed/star-lyra/400/400',
  'https://picsum.photos/seed/star-betel/400/400',
  'https://picsum.photos/seed/star-capella/400/400',
  'https://picsum.photos/seed/star-polaris/400/400',
];

const CONSTELL_DEFAULT_NAMES = [
  'α · SIRIUS',
  'β · VEGA',
  'γ · RIGEL',
  'δ · ALTAIR',
  'ε · LYRAE',
  'ζ · BETELGEUSE',
  'η · CAPELLA',
  'θ · POLARIS',
];

const CONSTELL_DEFAULT_MAG = ['-1.46', '0.03', '0.13', '0.77', '4.30', '0.50', '0.08', '1.98'];
const CONSTELL_DEFAULT_COORDS = [
  '06h 45m  −16°',
  '18h 36m  +38°',
  '05h 14m  −08°',
  '19h 50m  +08°',
  '18h 44m  +39°',
  '05h 55m  +07°',
  '05h 16m  +45°',
  '02h 31m  +89°',
];

// 黄道十二宫符号（Unicode），从白羊座开始逆时针摆放
const ZODIAC_SIGNS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];

export const ImageConstellationAtlas: React.FC<{
  images?: string[];
  names?: string[];
  magnitudes?: string[];
  coords?: string[];
  atlasTitle?: string;
  chapter?: string;
}> = ({
  images = CONSTELL_DEFAULT_IMAGES,
  names = CONSTELL_DEFAULT_NAMES,
  magnitudes = CONSTELL_DEFAULT_MAG,
  coords = CONSTELL_DEFAULT_COORDS,
  atlasTitle = 'ATLAS CŒLESTIS',
  chapter = 'Chapter IX · The Lantern Constellation',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // ---------- 布局参数 ----------
  const cx = width * 0.5;
  const cy = height * 0.55;
  const S = Math.min(width, height);

  // 星盘同心铜环半径序列（由内到外）
  const coreR = S * 0.055;
  const ringR1 = S * 0.10;
  const ringR2 = S * 0.15;
  const ringR3 = S * 0.20;
  const ringR4 = S * 0.26; // 黄道十二宫环
  const ringHalo = S * 0.38; // 最外光晕

  // 图片节点的光之轨道（椭圆，略大于黄道环，形成"光冠"环绕效果）
  const orbitRx = width * 0.35;
  const orbitRy = height * 0.28;

  // 节点数 & 节点半径
  const NODE_COUNT = 8;
  const nodeR = 60;

  // 整体缓慢自转（度 / 秒）
  const atlasRot = (frame / fps) * 2.4;
  const breathe = 1 + Math.sin(frame / 52) * 0.008;
  const radAtlas = (atlasRot * Math.PI) / 180;

  // 金色主色
  const GOLD = '#e8c57f';
  const GOLD_BRIGHT = '#fde3a7';
  const GOLD_DIM = 'rgba(232,197,127,0.55)';
  const GOLD_FAINT = 'rgba(232,197,127,0.28)';

  // ---------- 节点坐标（沿椭圆轨道均布，偏移 22.5° 避免纯正上/下/左/右） ----------
  const points = Array.from({ length: NODE_COUNT }).map((_, i) => {
    const baseAng = -Math.PI / 2 + Math.PI / 8 + (i / NODE_COUNT) * Math.PI * 2;
    const ang = baseAng + radAtlas * 0.6;
    // 轻微径向抖动让节点不完全对称，避免呆板
    const rMod = 1 + Math.sin(i * 1.73) * 0.04;
    const x = cx + Math.cos(ang) * orbitRx * rMod * breathe;
    const y = cy + Math.sin(ang) * orbitRy * rMod * breathe;
    return { x, y, ang };
  });

  // ---------- 背景星野（300 颗随机闪烁小星） ----------
  const starField = React.useMemo(() => {
    const arr: Array<{
      x: number; y: number; r: number; seed: number; tone: number;
    }> = [];
    for (let i = 0; i < 300; i++) {
      const s = Math.sin(i * 73.12) * 43758.5453;
      const t = Math.sin(i * 97.37) * 43758.5453;
      const u = Math.sin(i * 11.93) * 43758.5453;
      const v = Math.sin(i * 31.41) * 43758.5453;
      const rand = (x: number) => x - Math.floor(x);
      arr.push({
        x: rand(s) * width,
        y: rand(t) * height,
        r: 0.3 + rand(u) * 1.8,
        seed: i,
        tone: rand(v), // 0 白 / 0.5 金 / 1 蓝
      });
    }
    return arr;
  }, [width, height]);

  // ---------- 流星（3 道，不同时间、不同角度） ----------
  const meteors = [
    { s: 30, e: 80, fx: -120, fy: 60, tx: width * 0.6, ty: 280 },
    { s: 110, e: 162, fx: width + 120, fy: 130, tx: width * 0.25, ty: 360 },
    { s: 200, e: 258, fx: 40, fy: height + 40, tx: width * 0.85, ty: height * 0.18 },
  ];

  // 标题 & 装饰入场
  const headerOp = interpolate(frame, [0, 24], [0, 1], { extrapolateRight: 'clamp' });
  const framesOp = interpolate(frame, [6, 34], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 50%, #142048 0%, #0b1234 32%, #060a22 62%, #020410 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* ========== 背景：多层极光 / 银河尘云 ========== */}
      <div
        style={{
          position: 'absolute',
          top: '8%',
          left: '-12%',
          width: '124%',
          height: '78%',
          background:
            'radial-gradient(ellipse at 30% 45%, rgba(170,190,255,0.22) 0%, rgba(170,190,255,0.05) 40%, transparent 70%)',
          transform: `rotate(${-22 + atlasRot * 0.4}deg)`,
          filter: 'blur(26px)',
          mixBlendMode: 'screen',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 28% 36%, rgba(170,130,240,0.2) 0%, transparent 52%), radial-gradient(ellipse at 74% 62%, rgba(120,200,255,0.18) 0%, transparent 55%), radial-gradient(ellipse at 60% 80%, rgba(232,197,127,0.12) 0%, transparent 55%)',
          filter: 'blur(34px)',
          mixBlendMode: 'screen',
          pointerEvents: 'none',
        }}
      />

      {/* ========== 随机星野 ========== */}
      {starField.map((st) => {
        const tw = 0.5 + 0.5 * Math.sin(frame / 8 + st.seed * 1.3);
        const color =
          st.tone < 0.5 ? '255,255,255' : st.tone < 0.8 ? '255,230,180' : '170,200,255';
        return (
          <div
            key={`sf-${st.seed}`}
            style={{
              position: 'absolute',
              left: st.x,
              top: st.y,
              width: st.r,
              height: st.r,
              borderRadius: '50%',
              background: `rgb(${color})`,
              opacity: 0.22 + tw * 0.65,
              boxShadow: `0 0 ${st.r * 2.6}px rgba(${color},${tw * 0.55})`,
            }}
          />
        );
      })}

      {/* ========== 流星 ========== */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        <defs>
          {meteors.map((m, i) => (
            <linearGradient key={`mg-${i}`} id={`meteorGrad-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="70%" stopColor="#ffffff" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ffe3a0" stopOpacity="1" />
            </linearGradient>
          ))}
        </defs>
        {meteors.map((m, i) => {
          const t = interpolate(frame, [m.s, m.e], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          if (t <= 0 || t >= 1) return null;
          const hx = m.fx + (m.tx - m.fx) * t;
          const hy = m.fy + (m.ty - m.fy) * t;
          const dx = m.tx - m.fx;
          const dy = m.ty - m.fy;
          const len = 120;
          const nrm = Math.hypot(dx, dy) || 1;
          const tx = hx - (dx / nrm) * len;
          const ty = hy - (dy / nrm) * len;
          const fade =
            t < 0.15 ? t / 0.15 : t > 0.85 ? (1 - t) / 0.15 : 1;
          return (
            <g key={`meteor-${i}`} opacity={fade}>
              <line
                x1={tx}
                y1={ty}
                x2={hx}
                y2={hy}
                stroke={`url(#meteorGrad-${i})`}
                strokeWidth={2.4}
                strokeLinecap="round"
              />
              <circle cx={hx} cy={hy} r={4} fill="#fff" />
              <circle cx={hx} cy={hy} r={10} fill="#fff" opacity={0.3} />
            </g>
          );
        })}
      </svg>

      {/* ========== 中央巨型星盘 ========== */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      >
        <defs>
          <radialGradient id="coreHalo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,228,180,0.32)" />
            <stop offset="45%" stopColor="rgba(150,190,255,0.12)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="coreCenter" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff5d6" />
            <stop offset="50%" stopColor="rgba(232,197,127,0.55)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <linearGradient id="goldLine" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fde3a7" />
            <stop offset="50%" stopColor="#e8c57f" />
            <stop offset="100%" stopColor="#a27c35" />
          </linearGradient>
          <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,228,180,0.55)" />
            <stop offset="60%" stopColor="rgba(255,228,180,0.1)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>

        {/* 最外光晕 */}
        <circle cx={cx} cy={cy} r={ringHalo} fill="url(#coreHalo)" />

        {/* 黄道环（双线） */}
        <circle
          cx={cx}
          cy={cy}
          r={ringR4 + 6}
          fill="none"
          stroke={GOLD_DIM}
          strokeWidth={0.8}
        />
        <circle
          cx={cx}
          cy={cy}
          r={ringR4}
          fill="none"
          stroke={GOLD}
          strokeWidth={1.2}
          opacity={framesOp}
        />
        <circle
          cx={cx}
          cy={cy}
          r={ringR4 - 22}
          fill="none"
          stroke={GOLD_DIM}
          strokeWidth={0.8}
        />

        {/* 黄道 72 刻度（每 5° 一格） */}
        {Array.from({ length: 72 }).map((_, i) => {
          const a = (i * 5 * Math.PI) / 180 + radAtlas;
          const r1 = ringR4 - 2;
          const isMajor = i % 6 === 0;
          const isMid = i % 3 === 0;
          const r2 = ringR4 - (isMajor ? 18 : isMid ? 12 : 6);
          return (
            <line
              key={`tk-${i}`}
              x1={cx + Math.cos(a) * r1}
              y1={cy + Math.sin(a) * r1}
              x2={cx + Math.cos(a) * r2}
              y2={cy + Math.sin(a) * r2}
              stroke={isMajor ? GOLD : GOLD_DIM}
              strokeWidth={isMajor ? 1.1 : 0.6}
              opacity={framesOp}
            />
          );
        })}

        {/* 黄道十二宫符号 */}
        {ZODIAC_SIGNS.map((sg, i) => {
          const a = (i * 30 * Math.PI) / 180 - Math.PI / 2 + radAtlas;
          const r = ringR4 - 30;
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          return (
            <text
              key={`zd-${i}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fill={GOLD_BRIGHT}
              fontSize={13}
              opacity={0.75 * framesOp}
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {sg}
            </text>
          );
        })}

        {/* 放射 24 等分线（贯穿 ring3 → ring4 内） */}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * 15 * Math.PI) / 180 + radAtlas;
          return (
            <line
              key={`ray-${i}`}
              x1={cx + Math.cos(a) * ringR3}
              y1={cy + Math.sin(a) * ringR3}
              x2={cx + Math.cos(a) * (ringR4 - 32)}
              y2={cy + Math.sin(a) * (ringR4 - 32)}
              stroke={GOLD_FAINT}
              strokeWidth={0.6}
              opacity={framesOp}
            />
          );
        })}

        {/* 中环（虚线） */}
        <circle
          cx={cx}
          cy={cy}
          r={ringR3}
          fill="none"
          stroke={GOLD_DIM}
          strokeWidth={0.8}
          strokeDasharray="2 5"
          opacity={framesOp}
        />
        <circle
          cx={cx}
          cy={cy}
          r={ringR2}
          fill="none"
          stroke={GOLD}
          strokeWidth={0.8}
          opacity={framesOp * 0.7}
        />
        <circle
          cx={cx}
          cy={cy}
          r={ringR1}
          fill="none"
          stroke={GOLD_BRIGHT}
          strokeWidth={1.3}
          opacity={framesOp}
        />

        {/* 八芒星核心（两层旋转 45° 叠加） */}
        {[0, 45].map((baseRot) => (
          <g
            key={`star-${baseRot}`}
            transform={`translate(${cx} ${cy}) rotate(${baseRot + atlasRot * 0.5})`}
            opacity={framesOp}
          >
            <polygon
              points={(() => {
                const arr: string[] = [];
                for (let i = 0; i < 8; i++) {
                  const ang = (i * Math.PI) / 4;
                  const r = i % 2 === 0 ? coreR : coreR * 0.42;
                  arr.push(`${Math.cos(ang) * r},${Math.sin(ang) * r}`);
                }
                return arr.join(' ');
              })()}
              fill="none"
              stroke={GOLD_BRIGHT}
              strokeWidth={1.2}
            />
          </g>
        ))}
        <circle cx={cx} cy={cy} r={coreR * 0.55} fill="url(#coreCenter)" />
        <circle cx={cx} cy={cy} r={3.5} fill={GOLD_BRIGHT} />

        {/* 椭圆光之轨道（节点所在） */}
        <ellipse
          cx={cx}
          cy={cy}
          rx={orbitRx}
          ry={orbitRy}
          fill="none"
          stroke={GOLD_FAINT}
          strokeWidth={0.8}
          strokeDasharray="3 7"
          opacity={framesOp}
        />
      </svg>

      {/* ========== 节点 → 中心 的金色光束 ========== */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="beamGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fde3a7" stopOpacity="0" />
            <stop offset="100%" stopColor="#fde3a7" stopOpacity="0.45" />
          </linearGradient>
        </defs>
        {points.map((p, i) => {
          const delay = 14 + i * 6;
          const op = interpolate(frame - delay, [0, 22], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <line
              key={`beam-${i}`}
              x1={cx}
              y1={cy}
              x2={p.x}
              y2={p.y}
              stroke="url(#beamGrad)"
              strokeWidth={1.2}
              opacity={op * 0.75}
            />
          );
        })}
      </svg>

      {/* ========== 相邻节点间的星座链（依次点亮 + 流光） ========== */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="lineGradGold" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#a27c35" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#fde3a7" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#a27c35" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        {points.map((p, i) => {
          const next = points[(i + 1) % points.length]; // 形成闭环
          const appearAt = 40 + i * 8;
          const len = Math.hypot(next.x - p.x, next.y - p.y);
          const prog = interpolate(frame - appearAt, [0, 18], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <line
              key={`edge-${i}`}
              x1={p.x}
              y1={p.y}
              x2={next.x}
              y2={next.y}
              stroke="url(#lineGradGold)"
              strokeWidth={1.8}
              strokeDasharray={`${len * prog} ${len}`}
              strokeLinecap="round"
              opacity={0.9}
            />
          );
        })}

        {/* 流光粒子沿闭环连线飞驰 */}
        {(() => {
          const total = points.length;
          const t = (frame / (fps * 5)) % 1; // 5 秒跑完一圈
          const segFloat = t * total;
          const segIdx = Math.floor(segFloat);
          const segT = segFloat - segIdx;
          const a = points[segIdx % total];
          const b = points[(segIdx + 1) % total];
          const px = a.x + (b.x - a.x) * segT;
          const py = a.y + (b.y - a.y) * segT;
          return (
            <g>
              <circle cx={px} cy={py} r={12} fill="#fde3a7" opacity={0.22} />
              <circle cx={px} cy={py} r={6} fill="#fff6d9" opacity={0.6} />
              <circle cx={px} cy={py} r={2.5} fill="#ffffff" />
            </g>
          );
        })()}
      </svg>

      {/* ========== 8 个图片节点（大气金边 + 冷调星图风格） ========== */}
      {points.map((p, i) => {
        const delay = 18 + i * 7;
        const ent = spring({
          frame: frame - delay,
          fps,
          config: { damping: 14, stiffness: 150, mass: 0.9 },
        });
        const scale = interpolate(ent, [0, 1], [0.2, 1]);
        const op = interpolate(frame - delay, [0, 18], [0, 1], {
          extrapolateRight: 'clamp',
        });
        const twinkle = 0.88 + Math.sin(frame / 14 + i * 1.7) * 0.12;

        // 指向中心的径向单位向量（标签放外侧）
        const dx = p.x - cx;
        const dy = p.y - cy;
        const mag = Math.hypot(dx, dy) || 1;
        const nx = dx / mag;
        const ny = dy / mag;

        // 标签放置：上半环节点 → 标签在节点下方；下半环 → 标签在节点上方
        // 水平位置按 nx 方向微偏，避免标签吃进标题/底部条
        const labelAbove = ny > 0; // 下半球节点把标签放上方
        const labelHeight = 46; // 3 行标签大致高度
        const labelX = p.x + nx * 6;
        const labelY = labelAbove
          ? p.y - nodeR - 10 - labelHeight
          : p.y + nodeR + 10;

        const align: 'left' | 'right' | 'center' =
          nx > 0.35 ? 'left' : nx < -0.35 ? 'right' : 'center';
        const transformX =
          align === 'right'
            ? 'translateX(-100%)'
            : align === 'center'
            ? 'translateX(-50%)'
            : 'none';

        const roman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'][i] || '';

        return (
          <React.Fragment key={`node-${i}`}>
            {/* 外光晕 */}
            <div
              style={{
                position: 'absolute',
                left: p.x - nodeR * 2.2,
                top: p.y - nodeR * 2.2,
                width: nodeR * 4.4,
                height: nodeR * 4.4,
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(255,228,180,0.38) 0%, rgba(255,228,180,0) 60%)',
                opacity: op * twinkle,
                pointerEvents: 'none',
                mixBlendMode: 'screen',
              }}
            />
            {/* 十字光芒 */}
            <div
              style={{
                position: 'absolute',
                left: p.x - 110,
                top: p.y - 0.8,
                width: 220,
                height: 1.6,
                background:
                  'linear-gradient(90deg, transparent, rgba(255,236,190,0.85), transparent)',
                opacity: op * twinkle * 0.8,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: p.x - 0.8,
                top: p.y - 110,
                width: 1.6,
                height: 220,
                background:
                  'linear-gradient(180deg, transparent, rgba(255,236,190,0.85), transparent)',
                opacity: op * twinkle * 0.8,
                pointerEvents: 'none',
              }}
            />

            {/* 外金环（描边） */}
            <div
              style={{
                position: 'absolute',
                left: p.x - (nodeR + 10),
                top: p.y - (nodeR + 10),
                width: (nodeR + 10) * 2,
                height: (nodeR + 10) * 2,
                borderRadius: '50%',
                border: `1px solid ${GOLD_DIM}`,
                opacity: op,
                pointerEvents: 'none',
              }}
            />

            {/* 图片圆盘 */}
            <div
              style={{
                position: 'absolute',
                left: p.x - nodeR,
                top: p.y - nodeR,
                width: nodeR * 2,
                height: nodeR * 2,
                borderRadius: '50%',
                overflow: 'hidden',
                transform: `scale(${scale})`,
                opacity: op,
                border: `2.5px solid ${GOLD_BRIGHT}`,
                boxShadow:
                  '0 0 22px rgba(255,220,160,0.55), inset 0 0 14px rgba(0,0,0,0.45)',
                background: '#0a0d1a',
              }}
            >
              <Img
                src={images[i % images.length]}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'saturate(1.12) contrast(1.06) brightness(0.92)',
                }}
              />
              {/* 星图冷调覆盖 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(180deg, rgba(50,90,180,0.1) 0%, rgba(15,30,90,0.38) 100%)',
                }}
              />
              {/* 角落小罗马数字徽章 */}
              <div
                style={{
                  position: 'absolute',
                  top: 6,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  fontFamily: 'Georgia, serif',
                  fontSize: 10,
                  letterSpacing: 2,
                  color: GOLD_BRIGHT,
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                }}
              >
                {roman}
              </div>
            </div>

            {/* 外侧标签 */}
            <div
              style={{
                position: 'absolute',
                left: labelX,
                top: labelY,
                transform: transformX,
                fontFamily: 'Georgia, serif',
                opacity: op,
                textAlign: align === 'right' ? 'right' : align === 'left' ? 'left' : 'center',
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  letterSpacing: 3,
                  color: GOLD_BRIGHT,
                  textShadow: '0 2px 8px rgba(180,130,60,0.55)',
                }}
              >
                {names[i % names.length]}
              </div>
              <div
                style={{
                  marginTop: 2,
                  fontFamily: '"Courier New", monospace',
                  fontSize: 10,
                  letterSpacing: 2,
                  color: 'rgba(232,197,127,0.75)',
                }}
              >
                {coords[i % coords.length]}
              </div>
              <div
                style={{
                  marginTop: 1,
                  fontFamily: '"Courier New", monospace',
                  fontSize: 10,
                  letterSpacing: 1,
                  color: 'rgba(220,230,255,0.55)',
                }}
              >
                mag {magnitudes[i % magnitudes.length]}
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* ========== 四角装饰（古典书脊角花） ========== */}
      {framesOp > 0.01 &&
        [
          { x: 22, y: 22, rot: 0 },
          { x: width - 22, y: 22, rot: 90 },
          { x: width - 22, y: height - 22, rot: 180 },
          { x: 22, y: height - 22, rot: 270 },
        ].map((c, i) => (
          <svg
            key={`corner-${i}`}
            width={90}
            height={90}
            style={{
              position: 'absolute',
              left: c.x - (i === 0 || i === 3 ? 0 : 90),
              top: c.y - (i >= 2 ? 90 : 0),
              opacity: framesOp * 0.85,
              pointerEvents: 'none',
            }}
          >
            <g transform={`translate(45 45) rotate(${c.rot}) translate(-45 -45)`}>
              <path d="M6 6 L 60 6" stroke={GOLD} strokeWidth={1.2} />
              <path d="M6 6 L 6 60" stroke={GOLD} strokeWidth={1.2} />
              <path d="M6 6 L 40 40" stroke={GOLD_DIM} strokeWidth={0.6} />
              <circle cx={6} cy={6} r={3} fill="none" stroke={GOLD_BRIGHT} strokeWidth={1} />
              <circle cx={6} cy={6} r={1.2} fill={GOLD_BRIGHT} />
              <path
                d="M14 6 Q 22 14 14 22"
                fill="none"
                stroke={GOLD_DIM}
                strokeWidth={0.8}
              />
              <path
                d="M6 14 Q 14 22 22 14"
                fill="none"
                stroke={GOLD_DIM}
                strokeWidth={0.8}
              />
            </g>
          </svg>
        ))}

      {/* ========== 顶部徽章标题 ========== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerOp,
          zIndex: 100,
        }}
      >
        <div
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: 10,
            letterSpacing: 10,
            color: 'rgba(232,197,127,0.7)',
            textTransform: 'uppercase',
          }}
        >
          — Observatory Journal · Anno MMXXVI —
        </div>
        {/* 装饰线 + 标题组合 */}
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 22,
          }}
        >
          <div
            style={{
              height: 1,
              width: 140,
              background:
                'linear-gradient(90deg, transparent, rgba(232,197,127,0.85))',
            }}
          />
          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 34,
              letterSpacing: 18,
              color: GOLD_BRIGHT,
              textShadow:
                '0 2px 18px rgba(232,197,127,0.55), 0 0 32px rgba(180,130,60,0.35)',
              padding: '0 6px',
            }}
          >
            {atlasTitle}
          </div>
          <div
            style={{
              height: 1,
              width: 140,
              background:
                'linear-gradient(90deg, rgba(232,197,127,0.85), transparent)',
            }}
          />
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: 13,
            letterSpacing: 4,
            color: 'rgba(240,230,210,0.85)',
          }}
        >
          {chapter}
        </div>
      </div>

      {/* ========== 顶部左右 HUD 观测信息（紧凑两列，不占中间） ========== */}
      {(() => {
        const hudPanel = (
          label: string,
          entries: Array<[string, string]>,
          side: 'left' | 'right',
        ) => (
          <div
            style={{
              position: 'absolute',
              top: 96,
              [side]: 32,
              width: 132,
              opacity: framesOp * 0.9,
              fontFamily: 'Georgia, serif',
              color: 'rgba(232,197,127,0.85)',
              padding: '6px 8px',
              borderLeft: side === 'left' ? `1px solid ${GOLD_DIM}` : 'none',
              borderRight: side === 'right' ? `1px solid ${GOLD_DIM}` : 'none',
              textAlign: side === 'left' ? 'left' : 'right',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                fontSize: 9,
                letterSpacing: 4,
                textTransform: 'uppercase',
                color: GOLD_BRIGHT,
                marginBottom: 4,
              }}
            >
              {label}
            </div>
            {entries.map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
                  gap: 8,
                  fontSize: 9,
                  letterSpacing: 1,
                  lineHeight: 1.6,
                  color: 'rgba(240,230,210,0.9)',
                  fontFamily: '"Courier New", monospace',
                }}
              >
                <span
                  style={{
                    color: 'rgba(220,230,255,0.5)',
                    textTransform: 'uppercase',
                  }}
                >
                  {k}
                </span>
                <span style={{ color: '#f4ecd4' }}>{v}</span>
              </div>
            ))}
          </div>
        );
        return (
          <>
            {hudPanel(
              'Observatory',
              [
                ['Lat', '39°54′ N'],
                ['Lng', '116°23′ E'],
                ['Alt', '1920 m'],
              ],
              'left',
            )}
            {hudPanel(
              'Conditions',
              [
                ['See', '1.4″'],
                ['Hum', '42 %'],
                ['Tmp', '−3 °C'],
              ],
              'right',
            )}
          </>
        );
      })()}

      {/* ========== 底部观测仪刻度条 ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 18,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          opacity: framesOp,
        }}
      >
        <svg width={Math.min(width * 0.7, 720)} height={14}>
          {(() => {
            const W = Math.min(width * 0.7, 720);
            const ticks = 41;
            return (
              <>
                <line
                  x1={0}
                  y1={7}
                  x2={W}
                  y2={7}
                  stroke={GOLD_DIM}
                  strokeWidth={0.8}
                />
                {Array.from({ length: ticks }).map((_, i) => {
                  const x = (i / (ticks - 1)) * W;
                  const isMajor = i % 5 === 0;
                  return (
                    <line
                      key={`btk-${i}`}
                      x1={x}
                      y1={7}
                      x2={x}
                      y2={isMajor ? 14 : 11}
                      stroke={isMajor ? GOLD_BRIGHT : GOLD_DIM}
                      strokeWidth={isMajor ? 1 : 0.6}
                    />
                  );
                })}
              </>
            );
          })()}
        </svg>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 26,
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            letterSpacing: 4,
            color: 'rgba(232,197,127,0.75)',
            textTransform: 'uppercase',
          }}
        >
          <span>RA 00h</span>
          <span>·</span>
          <span>DEC +45°</span>
          <span>·</span>
          <span>Limiting Mag 6.5</span>
          <span>·</span>
          <span>Field 18°</span>
          <span>·</span>
          <span>Plate XXX</span>
        </div>
      </div>
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ============================================================
// #131 塔罗牌揭示 · ImageTarotReveal
// ------------------------------------------------------------
// 设计思路：
// 1. 深紫星云 + 金色粒子 + 微烛光氛围
// 2. 按凯尔特十字阵（Celtic Cross）摆放 7 张牌：
//    中心 / 中心横压 / 上 / 下 / 左 / 右 + 右侧 "未来之塔" 1 张
// 3. 每张牌先以"牌背"状态（金色符文花纹）落位
// 4. 延迟后逐张 3D 翻转（rotateY）揭示正面图片
// 5. 正面含金色神秘边框 + 罗马数字 + 阿卡纳牌名
// 6. 整体伴随柔和心跳式呼吸（scale 微动）
// ============================================================
const TAROT_DEFAULT_IMAGES = [
  'https://picsum.photos/seed/tarot-fool/420/720',
  'https://picsum.photos/seed/tarot-mage/420/720',
  'https://picsum.photos/seed/tarot-empress/420/720',
  'https://picsum.photos/seed/tarot-emperor/420/720',
  'https://picsum.photos/seed/tarot-hermit/420/720',
  'https://picsum.photos/seed/tarot-wheel/420/720',
  'https://picsum.photos/seed/tarot-star/420/720',
];

const TAROT_DEFAULT_NAMES = [
  'THE FOOL',
  'THE MAGICIAN',
  'THE EMPRESS',
  'THE EMPEROR',
  'THE HERMIT',
  'WHEEL OF FORTUNE',
  'THE STAR',
];

const TAROT_DEFAULT_NUMERALS = ['0', 'I', 'III', 'IV', 'IX', 'X', 'XVII'];

const TAROT_DEFAULT_KEYWORDS = [
  'Beginnings · Leap',
  'Power · Intent',
  'Abundance · Care',
  'Structure · Law',
  'Solitude · Insight',
  'Cycles · Change',
  'Hope · Clarity',
];

export const ImageTarotReveal: React.FC<{
  images?: string[];
  names?: string[];
  numerals?: string[];
  keywords?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = TAROT_DEFAULT_IMAGES,
  names = TAROT_DEFAULT_NAMES,
  numerals = TAROT_DEFAULT_NUMERALS,
  keywords = TAROT_DEFAULT_KEYWORDS,
  title = 'ARCANA · CELTIC CROSS',
  subtitle = 'A Reading in Seven Cards · MMXXVI',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const cardW = 150;
  const cardH = 240;
  const cx = width * 0.42;
  const cy = height * 0.52;

  // 7 张牌的位置 + 旋转 + 翻转延迟
  // 凯尔特十字：0 中心，1 中心横压，2 下，3 左，4 上，5 右，6 右侧未来塔
  const layout: Array<{
    x: number;
    y: number;
    rot: number;
    dropDelay: number;
    flipDelay: number;
  }> = [
    { x: cx, y: cy, rot: 0, dropDelay: 0, flipDelay: 60 },
    { x: cx, y: cy, rot: 90, dropDelay: 16, flipDelay: 72 },
    { x: cx, y: cy + cardH * 0.95, rot: 0, dropDelay: 26, flipDelay: 86 },
    { x: cx - cardW * 1.25, y: cy, rot: 0, dropDelay: 36, flipDelay: 100 },
    { x: cx, y: cy - cardH * 0.95, rot: 0, dropDelay: 46, flipDelay: 114 },
    { x: cx + cardW * 1.25, y: cy, rot: 0, dropDelay: 56, flipDelay: 128 },
    // 右侧未来塔（4 张位置中的合并版，这里只做 1 张作为顶端）
    {
      x: width * 0.82,
      y: cy - cardH * 0.55,
      rot: 0,
      dropDelay: 70,
      flipDelay: 150,
    },
  ];

  // 全景呼吸
  const breathe = 1 + Math.sin(frame / 38) * 0.008;

  // 粒子（金尘）
  const gold = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; r: number; seed: number }> = [];
    for (let i = 0; i < 70; i++) {
      const s = (n: number) => {
        const v = Math.sin(n * 9301 + 49297) * 233280;
        return v - Math.floor(v);
      };
      arr.push({
        x: s(i * 1.3) * width,
        y: s(i * 2.7) * height,
        r: 0.8 + s(i * 3.1) * 2.2,
        seed: i,
      });
    }
    return arr;
  }, [width, height]);

  const headerOp = interpolate(frame, [0, 26], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 50%, #2a1742 0%, #18082a 55%, #08030f 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 星云柔光 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 25% 30%, rgba(180,120,255,0.28) 0%, transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(255,170,120,0.18) 0%, transparent 50%)',
          mixBlendMode: 'screen',
          filter: 'blur(30px)',
          pointerEvents: 'none',
        }}
      />
      {/* 竖纹符文光晕 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(90deg, rgba(255,215,140,0.045) 0 1px, transparent 1px 60px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />
      {/* 金色粒子 */}
      {gold.map((g) => {
        const float = Math.sin(frame / 18 + g.seed) * 8;
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame / 10 + g.seed * 1.5));
        return (
          <div
            key={`g-${g.seed}`}
            style={{
              position: 'absolute',
              left: g.x,
              top: g.y + float,
              width: g.r,
              height: g.r,
              borderRadius: '50%',
              background: '#ffd98a',
              opacity: tw * 0.9,
              boxShadow: `0 0 ${g.r * 3}px rgba(255,200,110,${tw * 0.6})`,
            }}
          />
        );
      })}

      {/* 中心魔法圆阵 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      >
        <defs>
          <radialGradient id="sigilGrad">
            <stop offset="0%" stopColor="#ffd98a" stopOpacity="0" />
            <stop offset="80%" stopColor="#ffd98a" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#ffd98a" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g
          transform={`rotate(${frame / 3} ${cx} ${cy})`}
          opacity={headerOp * 0.55}
        >
          <circle cx={cx} cy={cy} r={290} stroke="#ffd98a" strokeWidth={0.8} fill="none" strokeDasharray="3 6" />
          <circle cx={cx} cy={cy} r={260} stroke="#ffd98a" strokeWidth={0.6} fill="none" />
          <circle cx={cx} cy={cy} r={220} stroke="#ffd98a" strokeWidth={0.5} fill="none" strokeDasharray="1 8" />
          {Array.from({ length: 12 }).map((_, i) => {
            const ang = (i * 30 * Math.PI) / 180;
            return (
              <g key={`sig-${i}`}>
                <line
                  x1={cx + Math.cos(ang) * 260}
                  y1={cy + Math.sin(ang) * 260}
                  x2={cx + Math.cos(ang) * 290}
                  y2={cy + Math.sin(ang) * 290}
                  stroke="#ffd98a"
                  strokeWidth={1}
                />
                <circle
                  cx={cx + Math.cos(ang) * 290}
                  cy={cy + Math.sin(ang) * 290}
                  r={2}
                  fill="#ffd98a"
                />
              </g>
            );
          })}
        </g>
      </svg>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerOp,
          zIndex: 200,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontStyle: 'italic',
            fontSize: 11,
            letterSpacing: 10,
            color: 'rgba(255,215,140,0.7)',
            textTransform: 'uppercase',
          }}
        >
          — A Reading at Midnight —
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: '"Georgia", serif',
            fontSize: 28,
            letterSpacing: 14,
            color: '#ffd98a',
            textShadow: '0 2px 14px rgba(255,160,70,0.55)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Georgia", serif',
            fontStyle: 'italic',
            fontSize: 12,
            letterSpacing: 5,
            color: 'rgba(255,220,180,0.7)',
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 阵位辅助线 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          opacity: headerOp * 0.3,
        }}
      >
        <line
          x1={cx - cardW * 1.9}
          y1={cy}
          x2={cx + cardW * 1.9}
          y2={cy}
          stroke="#ffd98a"
          strokeWidth={0.6}
          strokeDasharray="2 6"
        />
        <line
          x1={cx}
          y1={cy - cardH * 1.5}
          x2={cx}
          y2={cy + cardH * 1.5}
          stroke="#ffd98a"
          strokeWidth={0.6}
          strokeDasharray="2 6"
        />
      </svg>

      {/* 卡牌 */}
      {layout.map((L, i) => {
        // 下落入场
        const drop = spring({
          frame: frame - L.dropDelay,
          fps,
          config: { damping: 16, stiffness: 130, mass: 0.8 },
        });
        const dropY = interpolate(drop, [0, 1], [-40, 0]);
        const dropOp = interpolate(frame - L.dropDelay, [0, 14], [0, 1], {
          extrapolateRight: 'clamp',
        });

        // 翻转进度 0→1（0 牌背，1 正面）
        const flipRaw = interpolate(
          frame - L.flipDelay,
          [0, 28],
          [0, 180],
          {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }
        );
        // 正面可见当 >90°
        const faceUp = flipRaw > 90;

        // 中心牌轻微漂浮
        const float = Math.sin((frame + i * 20) / 40) * 2;

        return (
          <div
            key={`tarot-${i}`}
            style={{
              position: 'absolute',
              left: L.x - cardW / 2,
              top: L.y - cardH / 2 + dropY + float,
              width: cardW,
              height: cardH,
              opacity: dropOp,
              transform: `rotate(${L.rot}deg) scale(${breathe})`,
              transformOrigin: '50% 50%',
              perspective: '1200px',
              zIndex: i === 1 ? 120 : 100 - i,
            }}
          >
            {/* 翻转舞台 */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                transformStyle: 'preserve-3d',
                transform: `rotateY(${flipRaw}deg)`,
                transition: 'transform .3s',
              }}
            >
              {/* 牌背 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  borderRadius: 8,
                  background:
                    'linear-gradient(135deg, #2a1142 0%, #48236b 50%, #2a1142 100%)',
                  border: '2px solid #d9a94a',
                  boxShadow:
                    '0 12px 30px rgba(0,0,0,0.55), inset 0 0 0 3px rgba(255,220,150,0.2)',
                  overflow: 'hidden',
                }}
              >
                {/* 背面纹样 */}
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 150 240"
                  style={{ position: 'absolute', inset: 0 }}
                >
                  <defs>
                    <pattern
                      id={`bd-${i}`}
                      width="18"
                      height="18"
                      patternUnits="userSpaceOnUse"
                    >
                      <path
                        d="M9 0 L18 9 L9 18 L0 9 Z"
                        fill="none"
                        stroke="#d9a94a"
                        strokeWidth="0.5"
                        opacity="0.55"
                      />
                    </pattern>
                  </defs>
                  <rect
                    x="6"
                    y="6"
                    width="138"
                    height="228"
                    fill={`url(#bd-${i})`}
                  />
                  {/* 中央符文 */}
                  <g transform="translate(75 120)">
                    <circle
                      r="28"
                      fill="none"
                      stroke="#d9a94a"
                      strokeWidth="1"
                    />
                    <circle
                      r="22"
                      fill="none"
                      stroke="#d9a94a"
                      strokeWidth="0.5"
                      strokeDasharray="2 3"
                    />
                    <path
                      d="M0 -22 L22 0 L0 22 L-22 0 Z"
                      fill="none"
                      stroke="#d9a94a"
                      strokeWidth="0.8"
                    />
                    <circle r="3" fill="#d9a94a" />
                  </g>
                  {/* 四角小星 */}
                  {[
                    [20, 20],
                    [130, 20],
                    [20, 220],
                    [130, 220],
                  ].map(([sx, sy], k) => (
                    <g key={`cs-${k}`} transform={`translate(${sx} ${sy})`}>
                      <path
                        d="M0 -6 L1.5 -1.5 L6 0 L1.5 1.5 L0 6 L-1.5 1.5 L-6 0 L-1.5 -1.5 Z"
                        fill="#d9a94a"
                        opacity="0.9"
                      />
                    </g>
                  ))}
                </svg>
              </div>

              {/* 牌正面 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '2px solid #d9a94a',
                  background: '#0f0a1c',
                  boxShadow:
                    '0 14px 34px rgba(0,0,0,0.6), 0 0 18px rgba(255,190,100,0.35), inset 0 0 0 3px rgba(255,220,150,0.25)',
                }}
              >
                {/* 图片 */}
                <Img
                  src={images[i % images.length]}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(0.92) contrast(1.1) brightness(0.92)',
                  }}
                />
                {/* 金色内框 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 8,
                    border: '1px solid rgba(255,210,140,0.8)',
                    boxShadow: 'inset 0 0 0 1px rgba(255,230,180,0.15)',
                    pointerEvents: 'none',
                  }}
                />
                {/* 神秘暗蒙 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(40,10,60,0.35) 0%, rgba(30,10,40,0.05) 40%, rgba(20,5,30,0.55) 100%)',
                  }}
                />
                {/* 顶部罗马数字 */}
                <div
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontFamily: '"Georgia", serif',
                    fontSize: 15,
                    letterSpacing: 3,
                    color: '#ffd98a',
                    textShadow: '0 1px 4px rgba(0,0,0,0.9)',
                    fontWeight: 700,
                  }}
                >
                  · {numerals[i % numerals.length]} ·
                </div>
                {/* 底部牌名 */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontFamily: '"Georgia", serif',
                    fontSize: 11,
                    letterSpacing: 3,
                    color: '#ffd98a',
                    textShadow: '0 1px 4px rgba(0,0,0,0.9)',
                    fontWeight: 700,
                  }}
                >
                  {names[i % names.length]}
                </div>
                {/* 小关键词 */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontFamily: '"Georgia", serif',
                    fontStyle: 'italic',
                    fontSize: 8,
                    letterSpacing: 2,
                    color: 'rgba(255,230,180,0.75)',
                  }}
                >
                  {keywords[i % keywords.length]}
                </div>

                {/* 翻开瞬间的金色闪光 */}
                {faceUp && frame - L.flipDelay < 34 && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'radial-gradient(circle at center, rgba(255,220,140,0.55) 0%, rgba(255,220,140,0) 60%)',
                      opacity: interpolate(
                        frame - L.flipDelay,
                        [14, 20, 34],
                        [0, 0.9, 0],
                        { extrapolateRight: 'clamp' }
                      ),
                      mixBlendMode: 'screen',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* 右下角释义区 */}
      <div
        style={{
          position: 'absolute',
          right: 48,
          bottom: 60,
          width: 260,
          padding: '14px 18px',
          background:
            'linear-gradient(180deg, rgba(30,15,55,0.65), rgba(20,8,35,0.85))',
          border: '1px solid rgba(255,215,140,0.45)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
          opacity: headerOp,
          zIndex: 150,
        }}
      >
        <div
          style={{
            fontFamily: '"Georgia", serif',
            fontSize: 10,
            letterSpacing: 4,
            color: 'rgba(255,215,140,0.6)',
            textTransform: 'uppercase',
          }}
        >
          · Position · Crowning ·
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Georgia", serif',
            fontSize: 18,
            letterSpacing: 4,
            color: '#ffd98a',
          }}
        >
          {names[4 % names.length]}
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: '"Georgia", serif',
            fontStyle: 'italic',
            fontSize: 11,
            lineHeight: 1.5,
            color: 'rgba(255,230,200,0.78)',
          }}
        >
          "What you seek is patience. The lantern burns slow, yet the path it
          lights is certain. Listen to the quiet between the cards."
        </div>
      </div>

      {/* 左下角阵型说明 */}
      <div
        style={{
          position: 'absolute',
          left: 40,
          bottom: 22,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 3,
          color: 'rgba(255,215,140,0.55)',
          opacity: headerOp,
        }}
      >
        SPREAD · CELTIC CROSS · 7 CARDS · MMXXVI
      </div>

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.65) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};
